// PPTHeroScene.js
import { saveScoreToFirestore } from "../services/authService.js"; // Use authService directly
import { showGameOverUI } from "../ui/uiUtils.js";
import { savePlayerBaseline, saveLevelPerformance, getPlayerBaseline } from "../services/analyticsService.js";
import { populateAnalyticsOverlay } from "../ui/analyticsUI.js";
import UIController from "../ui/uiController.js";
import { auth, db, firebase } from "../firebaseConfig.js";
import sessionManager from "../services/SessionManager.js";
import CanvasFixer from '../canvasFixer.js'; // Adjust path as needed


export default class PPTHeroScene extends Phaser.Scene {
  constructor() {
    super({ key: "PPTHeroScene" });

    this.shortcutSequence = [];
    this.lastKeyTime = 0;
    this.sequenceTimeout = 3000; // ms before resetting multi-key combos

    this.levelComplete = false;
    this.topMessage = null;
    this.countdownEvent = null;
    this.groupDragStart = null;

    // For scoring/accuracy:
    this.levelClicks = 0;
    this.levelKeystrokes = 0;
    
    // Initialize analytics data structure properly
    this.analyticsData = {
      levelStartTime: 0,
      levelCompletionTime: 0,
      shortcutAttempts: 0,
      successfulShortcuts: 0,
      mouseClicks: 0,
      keystrokeCount: 0,
      errors: {
        misstypedShortcuts: 0,
        incorrectSequences: 0,
        missclicks: 0
      },
      shortcutsUsed: {},
      shortcutsMissed: {}
    };
    
    // Track if player has completed calibration before
    this.hasCompletedCalibration = false;
    this.skipCalibration = false;
    this.levelsPlayed = 0;
  }

  /**
   * Show desktop recommendation for mobile users
   */
  showDesktopRecommendation() {
    console.log('📱 PPT Hero: Showing desktop recommendation for mobile user');
    
    // Create mobile notification overlay
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
      color: white;
      padding: 24px;
      border-radius: 16px;
      border: 2px solid #00d4ff;
      z-index: 50000;
      max-width: 90vw;
      text-align: center;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    `;
    
    notification.innerHTML = `
      <div style="font-size: 24px; margin-bottom: 16px;">💻 Desktop Recommended</div>
      <p style="margin-bottom: 20px; line-height: 1.4;">
        PPT Hero is optimized for desktop use with full keyboard shortcuts and precision controls.
        For the best experience, please access from a desktop or laptop computer.
      </p>
      <div style="display: flex; gap: 12px; justify-content: center;">
        <button onclick="this.parentElement.parentElement.remove(); history.back();" style="
          background: #00d4ff;
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
        ">← Back to Menu</button>
        <button onclick="this.parentElement.parentElement.remove();" style="
          background: transparent;
          color: #00d4ff;
          border: 2px solid #00d4ff;
          padding: 12px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
        ">Continue Anyway</button>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 10000);
  }

// Fix the init method to properly handle data
init(data) {

  // Fix canvas visibility
CanvasFixer.fixCanvas();

// Force clean rendering state
this.renderer = this.game.renderer;
if (this.renderer) {
  // Reset renderer properties that might cause greying
  this.renderer.clearBeforeRender = true;
  this.renderer.preserveDrawingBuffer = false; // Changed in Chrome 97+
}

// Ensure the game container is visible
document.getElementById("game-container").style.filter = "none";
document.getElementById("game-container").style.opacity = "1";

  // Reset analytics and level data
  this.shortcutSequence = [];
  this.lastKeyTime = 0;
  this.levelComplete = false;
  this.topMessage = null;
  this.countdownEvent = null;
  
  // Reset scoring data
  this.levelClicks = 0;
  this.levelKeystrokes = 0;
  
  // Reset analytics tracking with proper timestamp
  this.analyticsData = {
    levelStartTime: Date.now() / 1000,
    levelCompletionTime: 0,
    shortcutAttempts: 0,
    successfulShortcuts: 0,
    mouseClicks: 0,
    keystrokeCount: 0,
    errors: {
      misstypedShortcuts: 0,
      incorrectSequences: 0,
      missclicks: 0
    },
    shortcutsUsed: {},
    shortcutsMissed: {}
  };

  // Check for forced calibration reset
  // ENHANCED: Check for user-specific force calibration reset flag
  const resetUser = auth.currentUser;
  const forceResetKey = resetUser ? `forceCalibrationReset_${resetUser.uid}` : 'forceCalibrationReset_guest';
  this.forceCalibrationReset = localStorage.getItem(forceResetKey) === 'true';
  if (this.forceCalibrationReset) {
    // Remove the flag after reading it
    localStorage.removeItem(forceResetKey);
    // Since we're forcing a reset, we'll need to pretend user hasn't calibrated  
    const resetCalibrationKey = resetUser ? `hasCompletedCalibration_${resetUser.uid}` : 'hasCompletedCalibration_guest';
    localStorage.setItem(resetCalibrationKey, 'false');
  }
  
  // Check for calibration status from localStorage
  // ENHANCED: Check for user-specific calibration completion status
  const user = auth.currentUser;
  const calibrationKey = user ? `hasCompletedCalibration_${user.uid}` : 'hasCompletedCalibration_guest';
  const hasCompletedCalibration = localStorage.getItem(calibrationKey) === 'true';
  this.hasCompletedCalibration = hasCompletedCalibration;
  
  // Check if we should skip calibration
  // First check if passed directly in data
  if (data && data.skipCalibration) {
    this.skipCalibration = true;
  } 
  // ENHANCED: Then check user-specific localStorage (set by the analytics UI)
  else {
    const skipUser = auth.currentUser;
    const skipCalibrationKey = skipUser ? `skipCalibration_${skipUser.uid}` : 'skipCalibration_guest';
    
    if (localStorage.getItem(skipCalibrationKey) === 'true') {
      this.skipCalibration = true;
      // Clear the flag after reading it (one-time use)
      localStorage.removeItem(skipCalibrationKey);
      console.log(`📊 Skip calibration flag found and cleared for user: ${skipUser ? skipUser.uid : 'guest'}`);
    } else {
      this.skipCalibration = false;
    }
  }
  
  console.log(`PPTHeroScene initialized. Skip calibration: ${this.skipCalibration}, Has completed calibration: ${this.hasCompletedCalibration}`);

  // Create a unique session ID if not exists
  if (!this.sessionId) {
    this.sessionId = Date.now().toString() + '-' + Math.random().toString(36).substr(2, 9);
  }
  
  // Initialize levelsPlayed counter if not set
  if (!this.levelsPlayed) {
    this.levelsPlayed = 0;
  }
  
  // Add resize handler
  this.handleResize = this.handleResize.bind(this);
  
}
// Add this new method to PPTHeroScene class
// (this is a completely new method)

prepareForRender() {
  // This method can be called from outside if greying is detected
  CanvasFixer.fixCanvas();
  
  // Reset camera color
  if (this.cameras && this.cameras.main) {
    this.cameras.main.alpha = 1;
    this.cameras.main.transparent = true; 
    
  }
  
  // Ensure scene is active
  if (this.scene) {
    this.scene.bringToTop();
  }
  
  // Force re-render
  if (this.game && this.game.renderer && typeof this.game.renderer.resetCanvas === 'function') {
    this.game.renderer.resetCanvas();
  }
}
// Also add this modified version of handleResize() if you don't already have it:
// (this should be a complete replacement of your handleResize method)

handleResize() {
  // Check if scene is still active
  if (!this.scene.isActive()) return;
  
  // Safety check for cameras
  if (!this.cameras || !this.cameras.main) return;
  
  try {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    
    // Update slide dimensions proportionally
    const slideWidth = width * 0.7;
    const slideHeight = height * 0.7;
    const slideX = width / 2;
    const slideY = height / 2;
    
    if (this.slideBackground) {
      this.slideBackground.setPosition(slideX, slideY);
      this.slideBackground.setSize(slideWidth, slideHeight);
    }
    
    // Update UI panel
    if (this.uiPanel) {
      this.uiPanel.setSize(width, 75);
    }
    
    // Reposition UI buttons - with individual safety checks
    if (this.startButton && this.startButton.scene === this) {
      this.startButton.setPosition(width - 420, 10);
    }
    
    if (this.restartButton && this.restartButton.scene === this) {
      this.restartButton.setPosition(width - 420, 40);
    }
    
    if (this.shortcutsButton && this.shortcutsButton.scene === this) {
      this.shortcutsButton.setPosition(width - 280, 10);
    }
    
    if (this.modeButton && this.modeButton.scene === this) {
      this.modeButton.setPosition(width - 280, 40);
    }
    
    if (this.analyticsButton && this.analyticsButton.scene === this) {
      this.analyticsButton.setPosition(width - 140, 10);
    }
    
    // Reposition shortcuts popup
    if (this.shortcutsPopup) {
      this.shortcutsPopup.setPosition(width / 2, height / 2);
    }
    
    // Force canvas fixer to run
    CanvasFixer.fixCanvas();
    
  } catch (e) {
    console.error("Error in handleResize:", e);
  }
}

// Find the shutdown method in PPTHeroScene.js and make sure it properly handles cleanup
shutdown() {
  // Remove event listeners - make this the FIRST thing you do
  this.scale.off('resize', this.handleResize, this);
  
  // Make sure we're not passing scene to ANY MenuUIUtils methods at this point
  
  // Remove keyboard listeners
  this.input.keyboard.off("keydown");
  
  // Remove pointer listeners
  this.input.off("dragstart");
  this.input.off("drag");
  this.input.off("dragend");
  this.input.off("pointerdown");
  this.input.off("pointermove");
  this.input.off("pointerup");
  
  // Clear any timers
  this.time.removeAllEvents();
  
  // End the session properly
  this.endSession();
  
  console.log("PPTHeroScene resources cleaned up");
}

  // Properly handle session end to track analytics
// Properly handle session end to track analytics
// Fix the endSession method to properly handle session data
endSession() {

   
  try {
    const user = auth.currentUser;
    const userId = user ? user.uid : null;
    
    if (userId && this.sessionId) {
      console.log("Ending session", this.sessionId, "for user", userId);
      
      // Find and update the session
      db.collection("sessions")
        .where("sessionId", "==", this.sessionId)
        .where("userId", "==", userId)
        .get()
        .then((snapshot) => {
          if (!snapshot.empty) {
            snapshot.forEach((doc) => {
              const sessionData = doc.data();
              console.log("Found session to update:", doc.id);
              
              // Use a safer approach to get startTime
              let startTime = new Date();
              if (sessionData.startTime) {
                // Handle both timestamp and date objects
                startTime = typeof sessionData.startTime.toDate === 'function' 
                  ? sessionData.startTime.toDate() 
                  : sessionData.startTime;
              }
              
              const duration = (Date.now() - startTime.getTime()) / 1000; // in seconds
              
              const sessionUpdate = {
                endTime: firebase.firestore.FieldValue.serverTimestamp(),
                duration: duration,
                levelsPlayed: this.levelsPlayed || 0,
                inProgress: false,
                score: this.score || 0,
                gameType: "PPTHero"
              };
              
              console.log("Updating session with:", sessionUpdate);
              
              doc.ref.update(sessionUpdate)
                .then(() => {
                  console.log("Session successfully updated");
                })
                .catch(error => {
                  console.error("Error updating session:", error);
                });
            });
          } else {
            console.log("No active session found to end");
          }
        })
        .catch(error => {
          console.error("Error finding session to end:", error);
        });
    }
  } catch (error) {
    console.error("Error in endSession:", error);
  }
}

async create() {
  
  // Ensure top ribbon is visible for this game scene
  const topRibbon = document.getElementById("top-ribbon");

  if (document.getElementById("top-ribbon")) {
  document.getElementById("top-ribbon").style.display = 'flex';
}
const gameContainer = document.getElementById("game-container");

// Adjust game container position
if (document.getElementById("game-container")) {
  document.getElementById("game-container").style.top = "50px";
  document.getElementById("game-container").style.height = "calc(100% - 50px)";
  gameContainer.style.filter = "none";
  gameContainer.style.opacity = "1";
}
  
  // Fix grey overlay issue - properly formatted code
  if (this.cameras && this.cameras.main) {
    this.cameras.main.alpha = 1;
    this.cameras.main.transparent = true;
    
  }
  

  // Use document.querySelector safely
  const canvas = document.querySelector('canvas');
  if (canvas) {
    canvas.style.opacity = '1';
    canvas.style.filter = 'none';
    
    // UNIFIED: Use centralized mobile detection and show desktop notification
    import('../services/MobileDetectionService.js').then(({ MobileDetectionService }) => {
      if (MobileDetectionService.isMobile()) {
        // Show desktop recommendation for PPT Hero
        this.showDesktopRecommendation();
        canvas.style.touchAction = "auto"; // Allow touch events
      }
    });
  }

  // REMOVED: Method moved inside class

  
  // Force scene to top of display list
  if (this.scene && typeof this.scene.bringToTop === 'function') {
    this.scene.bringToTop();
  }
  
  // Use CanvasFixer safely with error handling
  try {
    if (CanvasFixer && typeof CanvasFixer.fixAndFocusCanvas === 'function') {
      CanvasFixer.fixAndFocusCanvas();
    }
  } catch (e) {
    console.warn("Error using CanvasFixer:", e);
  }

  // Ensure the input system is working
  if (this.input && typeof this.input.enabled !== 'undefined') {
    this.input.enabled = true;
  }

  try {
    // Initialize session properly with error handling
    try {
      // Import sessionManager to ensure it's loaded
      const sessionManagerModule = await import("../services/SessionManager.js");
      const sessionManager = sessionManagerModule.default;
      sessionManager.init();
      console.log("Session initialized successfully");
    } catch (e) {
      console.error("Error initializing session in PPTHeroScene:", e);
      // Continue with scene creation even if session fails
    }
    
    // Check calibration status from the database if needed
    if (!this.hasCompletedCalibration) {
      await this.checkCalibrationStatus();
    }
    

    // Focus the Phaser canvas so keyboard events work
    this.game.canvas.setAttribute("tabindex", "0");
    this.game.canvas.focus();

    // Gray background => off-slide area
    //this.cameras.main.setBackgroundColor("#ccc");

    // White rectangle => slide
    const slideWidth = this.cameras.main.width * 0.7;
    const slideHeight = this.cameras.main.height * 0.7;
    const slideX = this.cameras.main.width / 2;
    const slideY = this.cameras.main.height / 2;
    this.slideBackground = this.add.rectangle(slideX, slideY, slideWidth, slideHeight, 0xffffff);
    this.slideBackground.setOrigin(0.5, 0.5);
    // 1) Gray rectangle (slightly bigger than the white slide)
const greyWidth = this.cameras.main.width;  // 30px margin on each side, for example
const greyHeight = this.cameras.main.height;
this.greyBackground = this.add.rectangle(
  slideX, 
  slideY, 
  greyWidth, 
  greyHeight, 
  0xcccccc
);
this.greyBackground.setOrigin(0.5, 0.5);

// Put it behind everything else:
this.greyBackground.setDepth(-9999);

// Ensure it cannot block clicks:
this.greyBackground.disableInteractive();



    // Create shortcuts help popup
    this.createShortcutsPopup();

    // Listen for keydown => handle shortcuts
    this.input.keyboard.on("keydown", (event) => {
      if (event.key === "Alt") {
        event.preventDefault();
        event.stopPropagation();
      }
      this.handleShortcuts(event);
    });

    // Define levels
    this.setupLevels();

    // Level mgmt & UI
    this.currentLevel = 0;
    this.score = 0;
    this.startTime = 0;
    this.timeLimit = 60; // seconds per level

    // CRITICAL: Don't create UI elements until scene is actually started
    // This prevents text elements from appearing when scene is loaded but not active
    // createUI() will be called in startLevel() when game actually starts

    // For shapes & selection
    this.allShapes = [];
    this.selectedShapes = [];
    this.isMarqueeSelecting = false;
    this.marqueeStartX = 0;
    this.marqueeStartY = 0;

    // Marquee rectangle
    this.marqueeRect = this.add
      .rectangle(0, 0, 0, 0, 0x0099ff, 0.2)
      .setStrokeStyle(1, 0x0099ff)
      .setOrigin(0, 0)
      .setVisible(false)
      .setDepth(9999);

    // Drag events
    this.input.on("dragstart", this.onDragStart, this);
    this.input.on("drag", this.onDrag, this);
    this.input.on("dragend", this.onDragEnd, this);

    // Pointer events => marquee
    this.input.on("pointerdown", this.onPointerDown, this);
    this.input.on("pointermove", this.onPointerMove, this);
    this.input.on("pointerup", this.onPointerUp, this);

    // Add resize handler
    this.scale.on('resize', this.handleResize, this);
    this.handleResize();

    // Add visibility change listener
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.scene.isActive()) {
        // Fix canvas when tab becomes visible again
        if (CanvasFixer && typeof CanvasFixer.fixPPTHeroScene === 'function') {
          CanvasFixer.fixPPTHeroScene(this);
        }
      }
    });

    // Start appropriate level based on skip flag
    if (this.skipCalibration) {
      console.log("Skipping calibration - starting at level 11");
      this.currentLevel = 11;
      this.startLevel(11);
    } else {
      // Start with level 0 (practice)
      this.startLevel(0);
    }
  } catch (error) {
    console.error("Error in PPTHeroScene create method:", error);
  }
}

// Add this new method to PPTHeroScene class
prepareForRender() {
  // Fix the canvas if needed (using your existing CanvasFixer)
  CanvasFixer.fixCanvas();

  // Reset the camera color explicitly
  if (this.cameras && this.cameras.main) {
    this.cameras.main.alpha = 1;
    this.cameras.main.transparent = true;
    
    
    // Force camera update
    this.cameras.main.dirty = true;
    this.cameras.main.preRender();
  }

  // Fix the game container by ensuring it has no background
  const gameContainer = document.getElementById("game-container");
  if (gameContainer) {
    gameContainer.style.filter = "none";
    gameContainer.style.opacity = "1";
    gameContainer.style.visibility = "visible";
    
    // CRITICAL: Clear any background color that might cause the grey overlay
    gameContainer.style.backgroundColor = "transparent";
    gameContainer.style.background = "none";
  }
  
  // Bring this scene to the top
  if (this.scene) {
    this.scene.bringToTop();
  }
  
  // Optional: Fix any slide background if it exists
  if (this.slideBackground) {
    this.slideBackground.setAlpha(1);
  }
  
  // Force a re-render if the renderer supports it
  if (this.game && this.game.renderer && typeof this.game.renderer.reset === 'function') {
    this.game.renderer.reset();
  }
}

  
  // Create shortcuts help popup
  createShortcutsPopup() {
    const instructions =
      "SHORTCUTS TO PRACTICE:\n\n" +
      "• Align Left: Alt,H,G,A,L\n" +
      "• Align Right: Alt,H,G,A,R\n" +
      "• Align Top: Alt,H,G,A,T\n" +
      "• Align Bottom: Alt,H,G,A,B\n" +
      "• Center Horizontally: Alt,H,G,A,C\n" +
      "• Center Vertically: Alt,H,G,A,M\n" +
      "• Distribute Horizontally: Alt,H,G,A,H\n" +
      "• Distribute Vertically: Alt,H,G,A,V\n\n" +
      "Layering:\n" +
      "• Ctrl+Shift+[ => Send to Back\n" +
      "• Ctrl+Shift+] => Bring to Front\n\n" +
      "TIP: Shift-click to multi-select, or drag a marquee.\n";

    // Define a style with wordWrap
    const popupMaxWidth = this.cameras.main.width * 0.8; // 80% of game width
    const style = {
      fontFamily: "Arial",
      fontSize: "16px",
      color: "#fff",
      align: "left",
      resolution: 2, // for sharper text
      wordWrap: { width: popupMaxWidth - 40 }
    };

    // Create the text object (invisible at first)
    const shortcutsText = this.add.text(0, 0, instructions, style).setOrigin(0.5);

    // Measure the text => size the background
    const textBounds = shortcutsText.getBounds();
    const bgWidth = textBounds.width + 40;
    const bgHeight = textBounds.height + 40;

    const finalWidth = Math.min(bgWidth, popupMaxWidth);
    const finalHeight = Math.min(bgHeight, this.cameras.main.height * 0.8);

    const backgroundRect = this.add.rectangle(
      0, 0,
      finalWidth,
      finalHeight,
      0x000000,
      0.8
    ).setOrigin(0.5);

    // Create a container, center it, add both items, hide by default
    this.shortcutsPopup = this.add.container(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      [backgroundRect, shortcutsText]
    );
    this.shortcutsPopup.setVisible(false);
    this.shortcutsPopup.setDepth(9999); // ensure it's on top
  }

  // Create UI elements
  createUI() {
    // Basic UI panel and text
    this.uiPanel = this.add.rectangle(0, 0, this.cameras.main.width, 75, 0x222222).setOrigin(0, 0);
    this.uiText = this.add.text(10, 10, "", {
      fontSize: "16px",
      color: "#fff",
      wordWrap: { width: 780 },
      
    });

    const buttonStyle = {
      fontSize: "16px",
      color: "#fff",
      backgroundColor: "#007BFF",   // default color (e.g., for Start Game)
      padding: { x: 8, y: 4 },
      fixedWidth: 120,              // ensure same width
      align: "center",
      resolution: window.devicePixelRatio
    };

    // "Start Game" button
    this.startButton = this.add.text(
      this.cameras.main.width - 420,
      10,
      "Start Game",
      buttonStyle
    );
    this.startButton.setInteractive({ useHandCursor: true });
    this.startButton.on("pointerdown", () => {
      // Clear practice shapes (level 0)
      if (this.currentLevel === 0) {
        this.allShapes.forEach(s => s.destroy());
        this.allShapes = [];

        // Reset score or anything else
        this.score = 0;

        // Show the Round 1 cut scene (no timers or shapes yet)
        this.showRoundOneIntro();
        this.startButton.disableInteractive(); 
      }
    });

    // "Restart" button => same style but different background color
    const restartStyle = {
      ...buttonStyle,
      backgroundColor: "#dc3545" // red
    };
    this.restartButton = this.add.text(
      this.cameras.main.width - 420,
      40,
      "Restart",
      restartStyle
    );
    this.restartButton.setInteractive({ useHandCursor: true });
    this.restartButton.on("pointerdown", () => {
      // Remove timers, destroy topMessage if needed
      this.time.removeAllEvents();
      if (this.topMessage) {
        this.topMessage.destroy();
        this.topMessage = null;
      }
      
      // Show skip calibration option if player has completed it before
      if (this.hasCompletedCalibration) {
        this.showSkipCalibrationPrompt();
      } else {
        this.scene.restart();
      }
    });

    // "Shortcuts" button => same style but yellow
    const shortcutsStyle = {
      ...buttonStyle,
      backgroundColor: "#FFFF00",
      color: "#000" // black text on yellow
    };
    this.shortcutsButton = this.add.text(
      this.cameras.main.width - 280,
      10,
      "Shortcuts",
      shortcutsStyle
    );
    this.shortcutsButton.setInteractive({ useHandCursor: true });
    this.shortcutsButton.on("pointerdown", () => {
      this.shortcutsPopup.setVisible(!this.shortcutsPopup.visible);
    });

    // "Mode" button placeholder under "Shortcuts"
    this.modeButton = this.add.text(
      this.cameras.main.width - 280,
      40,
      "Practice",
      shortcutsStyle
    );
    this.modeButton.setInteractive({ useHandCursor: true });

    // Analytics button
    const analyticsStyle = {
      ...buttonStyle,
      backgroundColor: "#17a2b8", // info blue
    };
    this.analyticsButton = this.add.text(
      this.cameras.main.width - 140,
      10,
      "Analytics",
      analyticsStyle
    );
    this.analyticsButton.setInteractive({ useHandCursor: true });
    this.analyticsButton.on("pointerdown", () => {
      this.showAnalyticsPopup();
    });
    // In PPTHeroScene.js - add this at the end of the createUI method

// Add admin button for admin users
const addAdminButton = () => {
  // Use the global window object to avoid import issues
  if (window.gameInstance && window.gameInstance.scene) {
    // Check if user is logged in
    const user = auth.currentUser;
    if (!user) return;
    
    // Check if user is admin
    db.collection("userProfiles").doc(user.uid).get().then(doc => {
      if (doc.exists && doc.data().role === "admin") {
        // Create admin button
        const adminButton = this.add.text(
          this.startButton.x - 140, // Position left of Start Game button
          this.startButton.y,      // Same Y as Start Game
          "Admin Mode",
          {
            fontSize: "16px",
            color: "#fff",
            backgroundColor: "#880000",
            padding: { x: 8, y: 4 },
            fixedWidth: 120,
            align: "center"
          }
        );
        
        // Make interactive
        adminButton.setInteractive({ useHandCursor: true });
        
        // Add click event
        adminButton.on("pointerdown", () => {
          // Save reference to this scene for the adminService
          window.adminScene = this;
          
          // Call toggleAdminMode from adminService
          if (window.toggleAdminMode) {
            window.toggleAdminMode(this);
          } else {
            // Fallback - import from adminService
            import("../services/adminService.js").then(module => {
              if (module.addAdminButtonToScene) {
                // Store button for later use
                this.adminButton = adminButton;
              }
            });
          }
        });
        
        // Store button for later reference
        this.adminButton = adminButton;
      }
    }).catch(error => {
      console.error("Error checking admin role:", error);
    });
  }
};

// Call the function to add the admin button
addAdminButton();
  }

  // Check if player has completed calibration before
  async checkCalibrationStatus() {
    const user = auth.currentUser;
    if (user) {
      try {
        const baseline = await getPlayerBaseline(user.uid);
        this.hasCompletedCalibration = baseline !== null;
        // ENHANCED: Set user-specific calibration completion status
        const saveUser = auth.currentUser;
        const saveCalibrationKey = saveUser ? `hasCompletedCalibration_${saveUser.uid}` : 'hasCompletedCalibration_guest';
        localStorage.setItem(saveCalibrationKey, this.hasCompletedCalibration ? 'true' : 'false');
      } catch (error) {
        console.error("Error checking calibration status:", error);
        this.hasCompletedCalibration = false;
      }
    } else {
      this.hasCompletedCalibration = false;
    }
  }

  // Show skip calibration prompt
  showSkipCalibrationPrompt() {
    // Create overlay
    const overlay = this.add.rectangle(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      this.cameras.main.width,
      this.cameras.main.height,
      0x000000,
      0.7
    );
    overlay.setDepth(9999);

    // Create prompt container
    const promptBg = this.add.rectangle(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
      400,
      200,
      0x333333
    );
    promptBg.setDepth(10000);

    const promptText = this.add.text(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2 - 50,
      "Would you like to skip the calibration round?",
      {
        fontSize: "20px",
        color: "#ffffff",
        align: "center",
        wordWrap: { width: 350 }
      }
    );
    promptText.setOrigin(0.5);
    promptText.setDepth(10000);

    // Yes button - skip calibration
    const yesBtn = this.add.text(
      this.cameras.main.width / 2 - 100,
      this.cameras.main.height / 2 + 30,
      "Yes, Skip",
      {
        fontSize: "18px",
        backgroundColor: "#28a745",
        color: "#ffffff",
        padding: { x: 10, y: 5 }
      }
    );
    yesBtn.setOrigin(0.5);
    yesBtn.setInteractive({ useHandCursor: true });
    yesBtn.setDepth(10000);
    yesBtn.on("pointerdown", () => {
      // Destroy prompt
      overlay.destroy();
      promptBg.destroy();
      promptText.destroy();
      yesBtn.destroy();
      noBtn.destroy();
      
      // Set skip flag and restart
      // ENHANCED: Set user-specific skip calibration flag
      const skipUser = auth.currentUser;
      const skipCalibrationKey = skipUser ? `skipCalibration_${skipUser.uid}` : 'skipCalibration_guest';
      localStorage.setItem(skipCalibrationKey, 'true');
      this.scene.restart();
    });

    // No button - don't skip
    const noBtn = this.add.text(
      this.cameras.main.width / 2 + 100,
      this.cameras.main.height / 2 + 30,
      "No, Restart",
      {
        fontSize: "18px",
        backgroundColor: "#dc3545",
        color: "#ffffff",
        padding: { x: 10, y: 5 }
      }
    );
    noBtn.setOrigin(0.5);
    noBtn.setInteractive({ useHandCursor: true });
    noBtn.setDepth(10000);
    noBtn.on("pointerdown", () => {
      // Destroy prompt
      overlay.destroy();
      promptBg.destroy();
      promptText.destroy();
      yesBtn.destroy();
      noBtn.destroy();
      
      // Restart without skipping
      // ENHANCED: Remove user-specific skip calibration flag
      const clearUser = auth.currentUser;
      const clearSkipCalibrationKey = clearUser ? `skipCalibration_${clearUser.uid}` : 'skipCalibration_guest';
      localStorage.removeItem(clearSkipCalibrationKey);
      this.scene.restart();
    });
  }

  // Show analytics popup
  async showAnalyticsPopup() {
    const user = auth.currentUser;
    if (!user) {
      // Show message for non-logged in users
      this.showMessage("Please log in to view your analytics.", 2000);
      return;
    }

    // Use analytics UI module
    try {
      populateAnalyticsOverlay(user.uid, "PPTHero");
    } catch (error) {
      console.error("Error showing analytics overlay:", error);
      this.showMessage("Error loading analytics. Please try again.", 2000);
    }
  }

  // Show temporary message
  showMessage(text, duration = 1500) {
    const centerX = this.cameras.main.width / 2;
    const centerY = this.cameras.main.height / 2;
    
    const msgBg = this.add.rectangle(
      centerX,
      centerY,
      400,
      100,
      0x000000,
      0.7
    );
    msgBg.setDepth(10000);
    
    const msg = this.add.text(
      centerX,
      centerY,
      text,
      {
        fontSize: "18px",
        color: "#ffffff",
        align: "center",
        wordWrap: { width: 380 }
      }
    );
    msg.setOrigin(0.5);
    msg.setDepth(10000);
    
    this.time.delayedCall(duration, () => {
      msgBg.destroy();
      msg.destroy();
    });
  }

  // Setup all the game levels
  setupLevels() {
    // This is where the levels array would be defined
    this.levels = [
      // LEVEL 0 (Practice)
      {
        instructions:
          "Practice your shortcuts! Move/align these 3 shapes.\nWhen ready, click 'Start Game' below.",
        shapes: [
          { x: 300, y: 200, w: 100, h: 100, color: 0x1ea7e1 },
          { x: 400, y: 300, w: 100, h: 100, color: 0x176b82 },
          { x: 500, y: 200, w: 100, h: 100, color: 0x6db832 },
        ],
        goal: () => false, // never completes automatically
      },
  
      // --- Round 1: Levels 1–10 (Calibration) ---
      //LEVEL 1
      {
        instructions: "Use Alt+H+G+A+R to align these shapes to the RIGHT edge of eachother!",
        shapes: [
          { x: 300, y: 200, w: 80, h: 100, color: 0x1ea7e1 },
          { x: 400, y: 300, w: 120, h: 80, color: 0x176b82 },
          { x: 250, y: 250, w: 90, h: 110, color: 0x6db832 },
        ],
        goal: (scene) => {
          // Check if all shapes are within 5px of the rightmost edge
          let maxRight = -Infinity;
          scene.allShapes.forEach((s) => {
            const right = s.x + s.displayWidth / 2;
            if (right > maxRight) maxRight = right;
          });
          
          // Success only if ALL shapes are aligned to the right edge
          for (let s of scene.allShapes) {
            const right = s.x + s.displayWidth / 2;
            if (Math.abs(right - maxRight) > 5) {
              return false;
            }
          }
          
          // Only count success if they used the right shortcut
          return scene.analyticsData.shortcutsUsed["alt,h,g,a,r"] > 0;
        }
      },
      {
        // LEVEL 2
        instructions: "Use Alt+H+G+A+L to align these shapes to the LEFT edge of eachother!",
        shapes: [
          { x: 500, y: 200, w: 60, h: 100, color: 0x1ea7e1 },
          { x: 600, y: 300, w: 120, h: 80, color: 0x176b82 },
          { x: 550, y: 400, w: 90, h: 110, color: 0x6db832 },
          { x: 480, y: 350, w: 70, h: 70, color: 0xff6600 },
        ],
        goal: (scene) => {
          // Find the leftmost edge
          let minLeft = Infinity;
          scene.allShapes.forEach((s) => {
            const left = s.x - s.displayWidth / 2;
            if (left < minLeft) minLeft = left;
          });
          
          // All shapes must be aligned to that left edge
          for (let s of scene.allShapes) {
            const left = s.x - s.displayWidth / 2;
            if (Math.abs(left - minLeft) > 5) return false;
          }
          
          // Verify the shortcut was used
          return scene.analyticsData.shortcutsUsed["alt,h,g,a,l"] > 0;
        }
      },
      // Level 3: Align to top edge
{
  instructions: "Use Alt+H+G+A+T to align these shapes to the TOP edge!",
  shapes: [
    { x: 250, y: 180, w: 100, h: 90, color: 0xff6600 },
    { x: 350, y: 220, w: 80, h: 120, color: 0xff9900 },
    { x: 450, y: 150, w: 110, h: 80, color: 0xffcc00 },
    { x: 550, y: 200, w: 70, h: 100, color: 0xffff00 },
    { x: 650, y: 170, w: 90, h: 70, color: 0x33cc33 },
  ],
  goal: (scene) => {
    // Calculate the topmost edge
    let minTop = Infinity;
    scene.allShapes.forEach((s) => {
      const top = s.y - s.displayHeight / 2;
      if (top < minTop) minTop = top;
    });
    
    // All shapes must be aligned to that top edge
    for (let s of scene.allShapes) {
      const top = s.y - s.displayHeight / 2;
      if (Math.abs(top - minTop) > 5) return false;
    }
    
    // Verify the shortcut was used
    return scene.analyticsData.shortcutsUsed["alt,h,g,a,t"] > 0;
  }
},
      
       // Level 4: Align to bottom edge
{
  instructions: "Use Alt+H+G+A+B to align these shapes to the BOTTOM edge!",
  shapes: [
    { x: 300, y: 350, w: 90, h: 110, color: 0x00ccff },
    { x: 400, y: 380, w: 120, h: 80, color: 0x0099cc },
    { x: 500, y: 360, w: 100, h: 90, color: 0x006699 },
    { x: 600, y: 400, w: 80, h: 120, color: 0x003366 },
    { x: 700, y: 330, w: 70, h: 70, color: 0x663399 },
  ],
  goal: (scene) => {
    // Find the bottom-most edge
    let maxBottom = -Infinity;
    scene.allShapes.forEach((s) => {
      const bottom = s.y + s.displayHeight / 2;
      if (bottom > maxBottom) maxBottom = bottom;
    });
    
    // All shapes must be aligned to that bottom edge
    for (let s of scene.allShapes) {
      const bottom = s.y + s.displayHeight / 2;
      if (Math.abs(bottom - maxBottom) > 5) return false;
    }
    
    // Verify the shortcut was used
    return scene.analyticsData.shortcutsUsed["alt,h,g,a,b"] > 0;
  }
},
      
        // Level 5: Horizontal distribution
{
  instructions: "Use Alt+H+G+A+H to evenly distribute these shapes horizontally!\n(Leftmost & rightmost remain fixed.)",
  shapes: [
    { x: 150, y: 300, w: 80, h: 80, color: 0xff3366 },
    { x: 250, y: 300, w: 80, h: 80, color: 0xff6699 },
    { x: 320, y: 300, w: 80, h: 80, color: 0xff99cc },
    { x: 450, y: 300, w: 80, h: 80, color: 0xffccff },
    { x: 500, y: 300, w: 80, h: 80, color: 0xff9900 },
    { x: 600, y: 300, w: 80, h: 80, color: 0xffcc00 },
  ],
  goal: (scene) => {
    // Sort shapes by x position
    const sorted = [...scene.allShapes].sort((a, b) => a.x - b.x);
    const leftX = sorted[0].x;
    const rightX = sorted[sorted.length - 1].x;
    
    // Check if middle shapes are evenly distributed
    const expectedGap = (rightX - leftX) / (sorted.length - 1);
    
    for (let i = 1; i < sorted.length - 1; i++) {
      const expectedX = leftX + expectedGap * i;
      if (Math.abs(sorted[i].x - expectedX) > 5) return false;
    }
    
    // Verify the shortcut was used
    return scene.analyticsData.shortcutsUsed["alt,h,g,a,h"] > 0;
  }
},
      // Level 6: Vertical distribution
{
  instructions: "Use Alt+H+G+A+V to evenly distribute these shapes vertically!\n(Topmost & bottommost remain fixed.)",
  shapes: [
    { x: 400, y: 100, w: 90, h: 90, color: 0x33cc33 },
    { x: 400, y: 180, w: 90, h: 90, color: 0x66cc66 },
    { x: 400, y: 250, w: 90, h: 90, color: 0x99cc99 },
    { x: 400, y: 340, w: 90, h: 90, color: 0xccffcc },
    { x: 400, y: 420, w: 90, h: 90, color: 0x33cc33 },
    { x: 400, y: 550, w: 90, h: 90, color: 0x66cc66 },
  ],
  goal: (scene) => {
    // Sort shapes by y position
    const sorted = [...scene.allShapes].sort((a, b) => a.y - b.y);
    const topY = sorted[0].y;
    const bottomY = sorted[sorted.length - 1].y;
    
    // Check if middle shapes are evenly distributed
    const expectedGap = (bottomY - topY) / (sorted.length - 1);
    
    for (let i = 1; i < sorted.length - 1; i++) {
      const expectedY = topY + expectedGap * i;
      if (Math.abs(sorted[i].y - expectedY) > 5) return false;
    }
    
    // Verify the shortcut was used
    return scene.analyticsData.shortcutsUsed["alt,h,g,a,v"] > 0;
  }
},
     // Level 7: Center Alignment
// Level 7: Center Alignment (Horizontal)
{
  instructions: "Use Alt+H+G+A+C to align all shapes in the CENTER!",
  shapes: [
    { x: 200, y: 200, w: 80, h: 100, color: 0x663399 },
    { x: 300, y: 300, w: 120, h: 80, color: 0x9966cc },
    { x: 400, y: 400, w: 90, h: 110, color: 0xcc99ff },
    { x: 500, y: 500, w: 70, h: 70, color: 0xff6600 },
  ],
  goal: (scene) => {
    // Check if the shortcut was used
    if (!(scene.analyticsData && 
          scene.analyticsData.shortcutsUsed && 
          scene.analyticsData.shortcutsUsed["alt,h,g,a,c"] > 0)) {
      return false;
    }
    
    // Check if all shapes share approximately the same x-coordinate (horizontal alignment)
    if (scene.allShapes.length <= 1) return true;
    
    // Get the x-coordinate of the first shape as reference
    const referenceX = scene.allShapes[0].x;
    const tolerance = 15; // Reasonable tolerance for alignment
    
    // Check if all other shapes have similar x-coordinates
    for (let i = 1; i < scene.allShapes.length; i++) {
      if (Math.abs(scene.allShapes[i].x - referenceX) > tolerance) {
        return false;
      }
    }
    
    return true;
  }
},

// Level 8: Vertical Center Alignment (Middle)
{
  instructions: "Use Alt+H+G+A+M to align all shapes in the MIDDLE!",
  shapes: [
    { x: 200, y: 150, w: 80, h: 80, color: 0xff3366 },
    { x: 350, y: 200, w: 80, h: 80, color: 0xff6699 },
    { x: 500, y: 250, w: 80, h: 80, color: 0x66cc66 },
    { x: 650, y: 300, w: 80, h: 80, color: 0x33cc33 },
  ],
  goal: (scene) => {
    // Check if the shortcut was used
    if (!(scene.analyticsData && 
          scene.analyticsData.shortcutsUsed && 
          scene.analyticsData.shortcutsUsed["alt,h,g,a,m"] > 0)) {
      return false;
    }
    
    // Check if all shapes share approximately the same y-coordinate (vertical alignment)
    if (scene.allShapes.length <= 1) return true;
    
    // Get the y-coordinate of the first shape as reference
    const referenceY = scene.allShapes[0].y;
    const tolerance = 15; // Reasonable tolerance for alignment
    
    // Check if all other shapes have similar y-coordinates
    for (let i = 1; i < scene.allShapes.length; i++) {
      if (Math.abs(scene.allShapes[i].y - referenceY) > tolerance) {
        return false;
      }
    }
    
    return true;
  }
},
     
    
      // LEVEL 9
      {
        instructions: "Level 9: Organize these shapes:\n1. Use Alt+H+G+A+T to align all BLUE rectangles to the TOP\n2. Use Alt+H+G+A+H to distribute them HORIZONTALLY\n3. Use Alt+H+G+A+C to align all GREEN squares to the CENTER\n4. Use Alt+H+G+A+V to distribute them VERTICALLY",
        shapes: [
          // Blue rectangles in random positions
          { x: 250, y: 150, w: 80, h: 50, color: 0x0000ff },
          { x: 350, y: 250, w: 80, h: 50, color: 0x0000ff },
          { x: 450, y: 180, w: 80, h: 50, color: 0x0000ff },
          { x: 550, y: 320, w: 80, h: 50, color: 0x0000ff },
          
          // Green squares of different sizes
          { x: 300, y: 300, w: 60, h: 60, color: 0x00ff00 },
          { x: 400, y: 400, w: 60, h: 60, color: 0x00ff00 },
          { x: 500, y: 500, w: 60, h: 60, color: 0x00ff00 }
        ],
        goal: (scene) => {
          // Log the entire analyticsData for debugging
          console.log("Analytics data:", scene.analyticsData);
          
          // Separate shapes into blue rectangles and green squares
          const blueRectangles = scene.allShapes.filter(s => s.fillColor === 0x0000ff);
          const greenSquares = scene.allShapes.filter(s => s.fillColor === 0x00ff00);
          
          console.log(`Found ${blueRectangles.length} blue rectangles and ${greenSquares.length} green squares`);
          
          // PART 1: Check if blue rectangles are aligned to the top
          const blueTopY = Math.min(...blueRectangles.map(s => s.y - s.displayHeight/2));
          const blueTopAligned = blueRectangles.every(s => 
            Math.abs((s.y - s.displayHeight/2) - blueTopY) < 30
          );
          
          console.log("Blue rectangles top aligned:", blueTopAligned);
          
          // PART 2: Check if blue rectangles are distributed horizontally
          let blueDistributed = true;
          if (blueRectangles.length > 2) {
            const sortedBlue = [...blueRectangles].sort((a, b) => a.x - b.x);
            const leftX = sortedBlue[0].x;
            const rightX = sortedBlue[sortedBlue.length - 1].x;
            const expectedGap = (rightX - leftX) / (sortedBlue.length - 1);
            
            // Check for relatively even spacing with a generous tolerance
            for (let i = 1; i < sortedBlue.length - 1; i++) {
              const expectedX = leftX + (expectedGap * i);
              if (Math.abs(sortedBlue[i].x - expectedX) > Math.max(40, expectedGap * 0.3)) {
                blueDistributed = false;
                break;
              }
            }
          }
          
          console.log("Blue rectangles horizontally distributed:", blueDistributed);
          
          // PART 3: Check if green squares are centered horizontally
          const centerX = scene.slideBackground.x;
          const greenCentered = greenSquares.every(s => 
            Math.abs(s.x - centerX) < 40
          );
          
          console.log("Green squares centered:", greenCentered);
          
          // PART 4: Check if green squares are distributed vertically
          let greenDistributed = true;
          if (greenSquares.length > 2) {
            const sortedGreen = [...greenSquares].sort((a, b) => a.y - b.y);
            const topY = sortedGreen[0].y;
            const bottomY = sortedGreen[sortedGreen.length - 1].y;
            const expectedGap = (bottomY - topY) / (sortedGreen.length - 1);
            
            // Check for relatively even spacing with a generous tolerance
            for (let i = 1; i < sortedGreen.length - 1; i++) {
              const expectedY = topY + (expectedGap * i);
              if (Math.abs(sortedGreen[i].y - expectedY) > Math.max(40, expectedGap * 0.3)) {
                greenDistributed = false;
                break;
              }
            }
          }
          
          console.log("Green squares vertically distributed:", greenDistributed);
          
          // Level is complete if all criteria are met
          const success = blueTopAligned && blueDistributed && greenCentered && greenDistributed;
          console.log("Level completed:", success);
          
          return success;
        }},
      
        // LEVEL 10
       // LEVEL 10

 // LEVEL 10
{
  instructions: "Level 10: Create a professional slide layout:\n1. Use Alt+H+G+A+T to align RED objects to the TOP (these are your title elements)\n2. Use Alt+H+G+A+B to align BLUE objects to the BOTTOM (footer elements)\n3. Use Ctrl+Shift+] to bring the YELLOW shapes to the FRONT\n4. Use Alt+H+G+A+M to align GREEN objects to the MIDDLE vertically\n5. Use Alt+H+G+A+H to distribute GREEN objects HORIZONTALLY",
  shapes: [
    // Title elements (RED)
    { x: 250, y: 150, w: 120, h: 40, color: 0xff0000 },
    { x: 480, y: 180, w: 100, h: 40, color: 0xff0000 },
    
    // Content elements (GREEN)
    { x: 180, y: 280, w: 80, h: 80, color: 0x00ff00 },
    { x: 350, y: 350, w: 80, h: 80, color: 0x00ff00 },
    { x: 520, y: 320, w: 80, h: 80, color: 0x00ff00 },
    
    // Decorative elements (YELLOW) - intended to be in front
    { x: 200, y: 200, w: 60, h: 60, color: 0xffff00 },
    { x: 500, y: 250, w: 60, h: 60, color: 0xffff00 },
    
    // Footer elements (BLUE)
    { x: 300, y: 450, w: 90, h: 30, color: 0x0000ff },
    { x: 450, y: 500, w: 90, h: 30, color: 0x0000ff }
  ],
  goal: (scene) => {
    // Separate shapes by color
    const redShapes = scene.allShapes.filter(s => s.fillColor === 0xff0000);
    const greenShapes = scene.allShapes.filter(s => s.fillColor === 0x00ff00);
    const yellowShapes = scene.allShapes.filter(s => s.fillColor === 0xffff00);
    const blueShapes = scene.allShapes.filter(s => s.fillColor === 0x0000ff);
    
    console.log(`Found: ${redShapes.length} red, ${greenShapes.length} green, ${yellowShapes.length} yellow, ${blueShapes.length} blue shapes`);
    
    // PART 1: Check if red shapes are roughly aligned to the top (very generous tolerance)
    const topQuarter = scene.slideBackground.y - scene.slideBackground.height * 0.25;
    const redTopAligned = redShapes.every(s => s.y < topQuarter);
    console.log("Red shapes top aligned:", redTopAligned);
    
    // PART 2: Check if blue shapes are roughly aligned to the bottom (very generous tolerance)
    const bottomQuarter = scene.slideBackground.y + scene.slideBackground.height * 0.25;
    const blueBottomAligned = blueShapes.every(s => s.y > bottomQuarter);
    console.log("Blue shapes bottom aligned:", blueBottomAligned);
    
    // PART 3: Simple check for yellow shapes being in front - just compare average depths
    const yellowAvgDepth = yellowShapes.reduce((sum, s) => sum + s.depth, 0) / yellowShapes.length;
    const otherAvgDepth = scene.allShapes
      .filter(s => s.fillColor !== 0xffff00)
      .reduce((sum, s) => sum + s.depth, 0) / (scene.allShapes.length - yellowShapes.length);
    const yellowInFront = yellowAvgDepth > otherAvgDepth;
    console.log("Yellow shapes in front:", yellowInFront);
    
    // PART 4: Check if green shapes are roughly in the middle vertically (very generous tolerance)
    const middleRange = scene.slideBackground.height * 0.4; // 40% of slide height
    const slideMiddleY = scene.slideBackground.y;
    const greenMiddleAligned = greenShapes.every(s => 
      Math.abs(s.y - slideMiddleY) < middleRange
    );
    console.log("Green shapes middle aligned:", greenMiddleAligned);
    
    // PART 5: Check if green shapes are somewhat horizontally distributed
    let greenDistributed = true;
    if (greenShapes.length > 2) {
      const sortedGreen = [...greenShapes].sort((a, b) => a.x - b.x);
      // Simple check: leftmost should be in left third, rightmost in right third
      const leftBound = scene.slideBackground.x - scene.slideBackground.width * 0.25;
      const rightBound = scene.slideBackground.x + scene.slideBackground.width * 0.25;
      greenDistributed = sortedGreen[0].x < leftBound && 
                         sortedGreen[sortedGreen.length-1].x > rightBound;
    }
    console.log("Green shapes horizontally distributed:", greenDistributed);
    
    // Verify all required shortcuts were used
    const shortcutsUsed = scene.analyticsData && scene.analyticsData.shortcutsUsed;
    const topAlignUsed = shortcutsUsed && shortcutsUsed["alt,h,g,a,t"] > 0;
    const bottomAlignUsed = shortcutsUsed && shortcutsUsed["alt,h,g,a,b"] > 0;
    const bringToFrontUsed = shortcutsUsed && shortcutsUsed["ctrl+shift+]"] > 0;
    const middleAlignUsed = shortcutsUsed && shortcutsUsed["alt,h,g,a,m"] > 0;
    const horizontalDistributeUsed = shortcutsUsed && shortcutsUsed["alt,h,g,a,h"] > 0;
    
    console.log("Shortcuts used - Top align:", topAlignUsed, "Bottom align:", bottomAlignUsed, 
                "Bring to front:", bringToFrontUsed, "Middle align:", middleAlignUsed,
                "Horizontal distribute:", horizontalDistributeUsed);
    
    // Level is complete if both visual criteria and shortcuts are satisfied
    const visualSuccess = redTopAligned && blueBottomAligned && yellowInFront && 
                         greenMiddleAligned && greenDistributed;
    const shortcutSuccess = topAlignUsed && bottomAlignUsed && bringToFrontUsed && 
                           middleAlignUsed && horizontalDistributeUsed;
    
    console.log("Visual criteria met:", visualSuccess);
    console.log("Shortcut criteria met:", shortcutSuccess);
    
    // Can use either one of these approaches:
    // 1. Require both visual and shortcut criteria (stricter)
    // return visualSuccess && shortcutSuccess;
    
    // 2. Require only that either criteria is met (more lenient)
    return visualSuccess || shortcutSuccess;
}
},
  
      // --- Round 2: Levels 11–20 (Advanced) ---
      {
        // LEVEL 11
        instructions:
          "Level 11: Align all green boxes to the right edge, all blue boxes to the left edge!",
        shapes: [
          { x: 300, y: 250, w: 80, h: 80, color: 0x00ff00 }, // green
          { x: 500, y: 350, w: 80, h: 80, color: 0x00ff00 }, // green
          { x: 400, y: 200, w: 80, h: 80, color: 0x0000ff }, // blue
          { x: 550, y: 300, w: 80, h: 80, color: 0x0000ff }, // blue
        ],
        goal: (scene) => {
          const greens = scene.allShapes.filter((s) => s.fillColor === 0x00ff00);
          const blues = scene.allShapes.filter((s) => s.fillColor === 0x0000ff);
  
          // 1) Greens near right edge
          let greenXs = greens.map((g) => g.x);
          let maxGreenX = Math.max(...greenXs);
          for (const gx of greenXs) {
            if (Math.abs(gx - maxGreenX) > 10) return false;
          }
  
          // 2) Blues near left edge
          let blueXs = blues.map((b) => b.x);
          let minBlueX = Math.min(...blueXs);
          for (const bx of blueXs) {
            if (Math.abs(bx - minBlueX) > 10) return false;
          }
          return true;
        },
      },
      {
        // LEVEL 12
        instructions:
          "Level 12: Red boxes on bottom edge (horizontally distributed), yellow boxes on top edge!",
        shapes: [
          // red
          { x: 200, y: 400, w: 60, h: 60, color: 0xff0000 },
          { x: 400, y: 450, w: 60, h: 60, color: 0xff0000 },
          { x: 600, y: 420, w: 60, h: 60, color: 0xff0000 },
          // yellow
          { x: 300, y: 100, w: 60, h: 60, color: 0xffff00 },
          { x: 500, y: 150, w: 60, h: 60, color: 0xffff00 },
        ],
        goal: (scene) => {
          const reds = scene.allShapes.filter((s) => s.fillColor === 0xff0000);
          const yellows = scene.allShapes.filter((s) => s.fillColor === 0xffff00);
  
          // 1) Reds near bottom
          let bottomY = scene.slideBackground.y + scene.slideBackground.height / 2;
          for (const r of reds) {
            if (bottomY - (r.y + r.displayHeight / 2) > 20) return false;
          }
          // horizontally distributed
          const sortedReds = reds.sort((a, b) => a.x - b.x);
          const leftX = sortedReds[0].x;
          const rightX = sortedReds[sortedReds.length - 1].x;
          let gapCount = sortedReds.length - 1;
          if (gapCount > 1) {
            let expectedGap = (rightX - leftX) / gapCount;
            for (let i = 1; i < sortedReds.length; i++) {
              let actualGap = sortedReds[i].x - sortedReds[i - 1].x;
              if (Math.abs(actualGap - expectedGap) > 15) return false;
            }
          }
  
          // 2) Yellows near top
          let topY = scene.slideBackground.y - scene.slideBackground.height / 2;
          for (const y of yellows) {
            if ((y.y - y.displayHeight / 2) - topY > 20) return false;
          }
          return true;
        },
      },
      // Add the remaining levels from 13 to 20
      {
        // LEVEL 13
        instructions: "Level 13: Half the shapes top-left, half bottom-right!",
        shapes: [
          { x: 200, y: 200, w: 80, h: 80, color: 0xff3366 },
          { x: 250, y: 300, w: 80, h: 80, color: 0xff6699 },
          { x: 600, y: 300, w: 80, h: 80, color: 0x66cc66 },
          { x: 650, y: 400, w: 80, h: 80, color: 0x33cc33 },
        ],
        goal: (scene) => {
          // 2 in top-left quadrant, 2 in bottom-right quadrant
          let topLeftCount = 0;
          let bottomRightCount = 0;
          scene.allShapes.forEach((s) => {
            if (s.x < 400 && s.y < 300) topLeftCount++;
            if (s.x > 400 && s.y > 300) bottomRightCount++;
          });
          return topLeftCount === 2 && bottomRightCount === 2;
        },
      },
      {
        // LEVEL 14
        instructions:
          "Level 14: Align all shapes to the right edge, except exactly one shape must remain on the left edge!",
        shapes: [
          { x: 200, y: 300, w: 60, h: 60, color: 0xff0000 },
          { x: 500, y: 200, w: 60, h: 60, color: 0x00ff00 },
          { x: 600, y: 400, w: 60, h: 60, color: 0x0000ff },
        ],
        goal: (scene) => {
          let leftCount = 0;
          let rightCount = 0;
          let slideRightX = scene.slideBackground.x + scene.slideBackground.width / 2;
          scene.allShapes.forEach((s) => {
            if (s.x < 250) leftCount++;
            if (Math.abs(s.x - (slideRightX - s.displayWidth / 2)) < 20) {
              rightCount++;
            }
          });
          return leftCount === 1 && rightCount === 2;
        },
      },
      {
        // LEVEL 15
        instructions:
          "Level 15: Evenly distribute these 5 shapes horizontally at the top edge!",
        shapes: [
          { x: 200, y: 150, w: 50, h: 50, color: 0xff6600 },
          { x: 300, y: 150, w: 50, h: 50, color: 0xff9900 },
          { x: 400, y: 150, w: 50, h: 50, color: 0xffcc00 },
          { x: 500, y: 150, w: 50, h: 50, color: 0xffff00 },
          { x: 600, y: 150, w: 50, h: 50, color: 0xffff66 },
        ],
        goal: (scene) => {
          // top alignment
          let topMost = Math.min(...scene.allShapes.map((s) => s.y));
          for (const s of scene.allShapes) {
            if (Math.abs(s.y - topMost) > 10) return false;
          }
          // horizontal distribution
          const sorted = scene.allShapes.sort((a, b) => a.x - b.x);
          const leftX = sorted[0].x;
          const rightX = sorted[sorted.length - 1].x;
          let gap = (rightX - leftX) / (sorted.length - 1);
          for (let i = 1; i < sorted.length; i++) {
            let actualGap = sorted[i].x - sorted[i - 1].x;
            if (Math.abs(actualGap - gap) > 15) return false;
          }
          return true;
        },
      },
      {
        // LEVEL 16
        instructions:
          "Level 16: Arrange these 4 shapes in a perfect 2x2 square in the center!",
        shapes: [
          { x: 200, y: 300, w: 50, h: 50, color: 0xff0000 },
          { x: 300, y: 300, w: 50, h: 50, color: 0x00ff00 },
          { x: 200, y: 400, w: 50, h: 50, color: 0x0000ff },
          { x: 300, y: 400, w: 50, h: 50, color: 0xffff00 },
        ],
        goal: (scene) => {
          // Expect them ~100px apart in x & y, near center
          let xs = scene.allShapes.map((s) => s.x).sort((a, b) => a - b);
          let ys = scene.allShapes.map((s) => s.y).sort((a, b) => a - b);
          if (Math.abs(xs[0] - xs[xs.length - 1]) < 80) return false;
          if (Math.abs(ys[0] - ys[ys.length - 1]) < 80) return false;
          return true;
        },
      },
      {
        // LEVEL 17
        instructions:
          "Level 17: Arrange red row on top, green row in the middle, blue row on bottom!",
        shapes: [
          { x: 250, y: 150, w: 50, h: 50, color: 0xff0000 }, // red
          { x: 350, y: 200, w: 50, h: 50, color: 0xff0000 }, // red
          { x: 450, y: 350, w: 50, h: 50, color: 0x00ff00 }, // green
          { x: 550, y: 300, w: 50, h: 50, color: 0x00ff00 }, // green
          { x: 400, y: 450, w: 50, h: 50, color: 0x0000ff }, // blue
          { x: 500, y: 400, w: 50, h: 50, color: 0x0000ff }, // blue
        ],
        goal: (scene) => {
          const reds = scene.allShapes.filter((s) => s.fillColor === 0xff0000);
          const greens = scene.allShapes.filter((s) => s.fillColor === 0x00ff00);
          const blues = scene.allShapes.filter((s) => s.fillColor === 0x0000ff);
  
          // red row => y < 250
          for (const r of reds) {
            if (r.y > 250) return false;
          }
          // green => 250 < y < 350
          for (const g of greens) {
            if (g.y < 250 || g.y > 350) return false;
          }
          // blue => y > 350
          for (const b of blues) {
            if (b.y < 350) return false;
          }
          return true;
        },
      },
      {
        // LEVEL 18
        instructions:
          "Level 18: Everything on the left edge except exactly 1 shape must be top-right corner!",
        shapes: [
          { x: 100, y: 200, w: 60, h: 60, color: 0xff6699 },
          { x: 150, y: 300, w: 60, h: 60, color: 0xff66cc },
          { x: 600, y: 100, w: 60, h: 60, color: 0xff99cc },
        ],
        goal: (scene) => {
          let leftCount = 0;
          let topRightCount = 0;
          let slideRightX = scene.slideBackground.x + scene.slideBackground.width / 2;
          let topEdgeY = scene.slideBackground.y - scene.slideBackground.height / 2;
  
          scene.allShapes.forEach((s) => {
            if (s.x < 250) leftCount++;
            let nearRight = Math.abs(s.x - (slideRightX - s.displayWidth / 2)) < 30;
            let nearTop = (s.y - s.displayHeight / 2) < topEdgeY + 30;
            if (nearRight && nearTop) topRightCount++;
          });
          return leftCount === 2 && topRightCount === 1;
        },
      },
      {
        // LEVEL 19
        instructions:
          "Level 19: Distribute shapes vertically. Leftmost pinned to left edge, rightmost pinned to right edge!",
        shapes: [
          { x: 200, y: 200, w: 50, h: 50, color: 0x663399 },
          { x: 400, y: 300, w: 50, h: 50, color: 0x9966cc },
          { x: 600, y: 400, w: 50, h: 50, color: 0xcc99ff },
        ],
        goal: (scene) => {
          const sorted = [...scene.allShapes].sort((a, b) => a.y - b.y);
          let topY = sorted[0].y;
          let bottomY = sorted[sorted.length - 1].y;
          // even spacing
          if (sorted.length > 2) {
            let expectedGap = (bottomY - topY) / (sorted.length - 1);
            for (let i = 1; i < sorted.length; i++) {
              let actualGap = sorted[i].y - sorted[i - 1].y;
              if (Math.abs(actualGap - expectedGap) > 10) return false;
            }
          }
          // leftmost pinned left
          let leftShape = sorted[0];
          if (leftShape.x > 250) return false;
  
          // rightmost pinned right
          let rightShape = sorted[sorted.length - 1];
          let slideRight = scene.slideBackground.x + scene.slideBackground.width / 2;
          if (Math.abs(rightShape.x - (slideRight - rightShape.displayWidth / 2)) > 20) {
            return false;
          }
          return true;
        },
      },
      {
        // LEVEL 20
        instructions:
          "Level 20: Reds on the left, greens distributed vertically in the center, blues on bottom edge!",
        shapes: [
          // red
          { x: 100, y: 200, w: 60, h: 60, color: 0xff0000 },
          { x: 200, y: 300, w: 60, h: 60, color: 0xff0000 },
          { x: 300, y: 100, w: 60, h: 60, color: 0xff0000 },
          // green
          { x: 400, y: 200, w: 60, h: 60, color: 0x00ff00 },
          { x: 500, y: 300, w: 60, h: 60, color: 0x00ff00 },
          { x: 600, y: 100, w: 60, h: 60, color: 0x00ff00 },
          // blue
          { x: 700, y: 200, w: 60, h: 60, color: 0x0000ff },
          { x: 300, y: 400, w: 60, h: 60, color: 0x0000ff },
          { x: 500, y: 450, w: 60, h: 60, color: 0x0000ff },
        ],
        goal: (scene) => {
          const reds = scene.allShapes.filter((s) => s.fillColor === 0xff0000);
          const greens = scene.allShapes.filter((s) => s.fillColor === 0x00ff00);
          const blues = scene.allShapes.filter((s) => s.fillColor === 0x0000ff);
  
          // 1) Reds near left
          for (const r of reds) {
            if (r.x > 300) return false;
          }
          // 2) Greens distributed vertically in center (~ x=400..450)
          if (greens.length > 1) {
            let sortedG = greens.sort((a, b) => a.y - b.y);
            let gap = (sortedG[sortedG.length - 1].y - sortedG[0].y) / (greens.length - 1);
            for (let i = 1; i < sortedG.length; i++) {
              let actualGap = sortedG[i].y - sortedG[i - 1].y;
              if (Math.abs(actualGap - gap) > 15) return false;
            }
            for (const g of greens) {
              if (g.x < 350 || g.x > 450) return false;
            }
          }
          // 3) Blues near bottom
          let slideBottomY = scene.slideBackground.y + scene.slideBackground.height / 2;
          for (const b of blues) {
            if (slideBottomY - (b.y + b.displayHeight / 2) > 20) return false;
          }
          return true;
        },
      },
    ];
  }

  // Start level with analytics tracking
  startLevel(levelIndex) {
    // CRITICAL: Create UI elements when game actually starts (not on scene load)
    if (!this.uiText) {
      this.createUI();
    }
    
    // Increment levels played counter
    this.levelsPlayed++;
    
    // Clear old shapes
    this.allShapes.forEach((s) => s.destroy());
    this.allShapes = [];
    this.selectedShapes = [];
    this.levelComplete = false;
    this.levelClicks = 0;
    this.levelKeystrokes = 0;
    
    // Set start time and ensure it's in seconds for consistency
    this.startTime = Date.now() / 1000;
    
    // Reset analytics tracking with consistent timing format
    this.analyticsData = {
      levelStartTime: Date.now() / 1000,
      levelCompletionTime: 0,
      shortcutAttempts: 0,
      successfulShortcuts: 0,
      mouseClicks: 0,
      keystrokeCount: 0,
      errors: {
        misstypedShortcuts: 0,
        incorrectSequences: 0,
        missclicks: 0
      },
      shortcutsUsed: {},
      shortcutsMissed: {}
    };
  
    // Destroy any leftover "shortcut list" text from practice
    if (this.shortcutListText) {
      this.shortcutListText.destroy();
      this.shortcutListText = null;
    }
  
    // IMPORTANT: Get levelData BEFORE using it
    const levelData = this.levels[levelIndex];
    if (!levelData) {
      // No more levels => final
      return;
    }
    
    // Call onLevelStart function if it exists
    if (levelData.onLevelStart && typeof levelData.onLevelStart === 'function') {
      levelData.onLevelStart(this);
    }
  
    // Update mode display
    if (this.modeButton) {
      if (levelIndex === 0) {
        this.modeButton.setText("Practice");
      } else if (levelIndex >= 1 && levelIndex <= 10) {
        this.modeButton.setText("Calibration");
      } else {
        this.modeButton.setText("Advanced");
      }
    }
  
    // Randomly place shapes within the slide
    const playAreaX = this.slideBackground.x - this.slideBackground.width / 2;
    const playAreaY = this.slideBackground.y - this.slideBackground.height / 2;
    const playAreaWidth = this.slideBackground.width;
    const playAreaHeight = this.slideBackground.height;
    const placedShapes = [];
  
    const isOverlapping = (newX, newY, newW, newH) => {
      return placedShapes.some(({ x, y, w, h }) => {
        return newX < x + w && newX + newW > x && newY < y + h && newY + newH > y;
      });
    };
  
    // Create shapes
    levelData.shapes.forEach((cfg) => {
      let attempts = 50;
      let newX, newY;
      
      // If the shape is fixed (like a divider), use its exact position
      if (cfg.isFixed) {
        this.createShape(cfg.x, cfg.y, cfg.w, cfg.h, cfg.color, true, cfg.isDecoration || false);
        placedShapes.push({ x: cfg.x, y: cfg.y, w: cfg.w, h: cfg.h });
      } else {
        // For movable shapes, place them in random non-overlapping positions
        do {
          newX = Phaser.Math.Between(playAreaX + cfg.w / 2, playAreaX + playAreaWidth - cfg.w / 2);
          newY = Phaser.Math.Between(playAreaY + cfg.h / 2, playAreaY + playAreaHeight - cfg.h / 2);
          attempts--;
        } while (isOverlapping(newX, newY, cfg.w, cfg.h) && attempts > 0);
  
        if (attempts > 0) {
          this.createShape(newX, newY, cfg.w, cfg.h, cfg.color, false, cfg.isDecoration || false);
          placedShapes.push({ x: newX, y: newY, w: cfg.w, h: cfg.h });
        }
      }
    });
  
    // Show instructions
    this.uiText.setText(
      `Level ${levelIndex}: ${levelData.instructions}\nScore: ${this.score}`
    );
  }
  // Update method to check for level completion
  update() {
    if (this.levelComplete) return;

    // Use consistent time format (seconds)
    const elapsed = (Date.now() / 1000) - this.startTime;
    const remaining = Math.max(0, this.timeLimit - elapsed);

    // Add null check for this.levels
    if (!this.levels || !this.levels[this.currentLevel]) return;

    const levelData = this.levels[this.currentLevel];
    // Already checked above that levelData exists

    // Update UI text
    if (this.currentLevel === 0) {
      // practice
      this.uiText.setText(
        `Level 0 (Practice): ${levelData.instructions}\nScore: ${this.score}`
      );
    } else {
      this.uiText.setText(
        `Level ${this.currentLevel}: ${levelData.instructions}\nScore: ${this.score}   Time: ${Math.floor(
          remaining
        )}s`
      );
    }

    // Time up => penalize & move on
    if (this.currentLevel !== 0 && remaining <= 0) {
      this.score -= 50;
      
      // Save level performance data with timeout
      this.saveLevelAnalytics(false);
      
      this.nextLevelImmediate();
      return;
    }

    // If the goal is met => levelCleared
    if (levelData.goal && typeof levelData.goal === 'function' && levelData.goal(this) && !this.levelComplete) {
      this.levelCleared();
    }
  }

  // Level cleared with analytics
  levelCleared() {
    this.levelComplete = true;
    const elapsedTime = (Date.now() / 1000) - this.startTime;
    const speedBonus = Math.max(0, Math.floor((this.timeLimit - elapsedTime) * 2));
    const clickPenalty = Math.max(0, (this.levelClicks - 1) * 5);
    const keystrokePenalty = Math.max(0, (this.levelKeystrokes - 5) * 2);
    const baseBonus = 100;
    let totalBonus = baseBonus + speedBonus - clickPenalty - keystrokePenalty;
    if (totalBonus < 0) totalBonus = 0;
  
    // KEYBOARD-ONLY BONUS
    if (this.levelClicks === 0) {
      totalBonus = Math.floor(totalBonus * 1.5); // 1.5x multiplier
    }
  
    this.score += totalBonus;
    
    // Set the completion time before saving analytics
    this.analyticsData.levelCompletionTime = elapsedTime;

    // Save level analytics
    this.saveLevelAnalytics(true);

    // Animate shapes
    this.selectedShapes = [];
    this.allShapes.forEach((s) => {
      this.tweens.add({
        targets: s,
        scaleX: 1.2,
        scaleY: 1.2,
        yoyo: true,
        duration: 500,
        ease: "Sine.easeInOut",
      });
    });

    // Show "Slide cleared!" text
    const centerX = this.cameras.main.width / 2;
    if (!this.topMessage) {
      this.topMessage = this.add
        .text(centerX, 100, `Slide cleared! +${totalBonus} pts`, {
          fontSize: "16px",
          color: "#000",
        })
        .setOrigin(0.5, 0);
    } else {
      this.topMessage.setText(`Slide cleared! +${totalBonus} pts`);
    }

    // After 2 seconds => countdown to next level
    this.time.delayedCall(2000, () => {
      // If the scene got restarted/shutdown, do nothing
      if (!this.scene.isActive()) return;
      if (!this.topMessage) return;
    
      let countdown = 3;
      this.topMessage.setText(`Next level starting in ${countdown}...`);
    
      this.countdownEvent = this.time.addEvent({
        delay: 1000,
        repeat: 2,
        callback: () => {
          // Scene might have been restarted in these 3 seconds
          if (!this.scene.isActive()) return;
          if (!this.topMessage) return;
    
          countdown--;
          if (countdown > 0) {
            this.topMessage.setText(`Next level starting in ${countdown}...`);
          } else {
            this.topMessage.setText("");
            this.levelComplete = false;
            this.nextLevelImmediate();
          }
        },
      });
    });
  }

  // Save analytics data for the level
  // src/scenes/PPTHeroScene.js

  // --- REPLACE THIS METHOD ---
  saveLevelAnalytics(completed) {
    const user = auth.currentUser;
    const userId = user ? user.uid : null;

    // Skip analytics for non-logged in users or practice level
    if (!userId || this.currentLevel === 0) {
         console.log(`Analytics skipped (User: ${userId ? 'OK' : 'None'}, Level: ${this.currentLevel})`);
         return;
    }

    // Ensure analyticsData structure exists and is initialized
    if (!this.analyticsData) {
         console.error("Cannot save level analytics: this.analyticsData is missing!");
         // Attempt to re-initialize analyticsData partially if possible
         this.analyticsData = {
             levelStartTime: this.startTime || (Date.now() / 1000), // Use scene startTime or now
             levelCompletionTime: completed ? ((Date.now() / 1000) - (this.startTime || (Date.now() / 1000))) : 0,
             shortcutAttempts: 0, successfulShortcuts: 0, mouseClicks: 0, keystrokeCount: 0,
             errors: { misstypedShortcuts: 0, incorrectSequences: 0, missclicks: 0 },
             shortcutsUsed: {}, shortcutsMissed: {}
         };
         // Return early as data might be incomplete
         return;
    }

    // --- *** FIX: Calculate Accuracy *** ---
    const totalAttempts = Math.max(1, this.analyticsData.shortcutAttempts || 0); // Avoid division by zero
    const accuracy = Math.round(((this.analyticsData.successfulShortcuts || 0) / totalAttempts) * 100);
    // --- *** END FIX *** ---

    // Calculate completion time robustly
    const completionTime = this.analyticsData.levelCompletionTime || ((Date.now() / 1000) - this.analyticsData.levelStartTime);

    // Ensure errors object and its properties exist
    const errors = this.analyticsData.errors || {};
    const misstypedShortcuts = errors.misstypedShortcuts || 0;
    const incorrectSequences = errors.incorrectSequences || 0;
    const missclicks = errors.missclicks || 0;

    // Prepare level data with calculated accuracy
    const levelData = {
      userId: userId,
      sessionId: this.sessionId || sessionManager.getSessionId(), // Get current session ID
      levelNumber: this.currentLevel,
      score: this.score, // Current total score at level end
      completionTime: completionTime,
      accuracy: accuracy, // *** ADDED ACCURACY ***
      successfulShortcuts: this.analyticsData.successfulShortcuts || 0,
      shortcutAttempts: this.analyticsData.shortcutAttempts || 0,
      mouseClicks: this.analyticsData.mouseClicks || 0,
      keystrokeCount: this.analyticsData.keystrokeCount || 0,
      errors: { // Ensure errors are included
        misstypedShortcuts: misstypedShortcuts,
        incorrectSequences: incorrectSequences,
        missclicks: missclicks
      },
      shortcutsUsed: this.analyticsData.shortcutsUsed || {},
      shortcutsMissed: this.analyticsData.shortcutsMissed || {},
      completed: completed, // Whether the level was successfully completed
      // timestamp will be added by the service
    };

    console.log(`Saving Level ${this.currentLevel} Analytics:`, levelData);

    // Save to Firebase via analyticsService
    saveLevelPerformance(userId, "PPTHero", levelData) // Ensure "PPTHero" is passed
      .then(() => {
        console.log(`Successfully saved level ${this.currentLevel} performance data!`);
      })
      .catch(error => {
        console.error(`Failed to save level ${this.currentLevel} performance:`, error);
      });

    // --- Baseline Saving Logic (Keep as is) ---
    // Check if this is the end of calibration round (level 10) and it was completed successfully
    if (completed && this.currentLevel === 10) {
        const forceReset = localStorage.getItem('forceCalibrationReset') === 'true';
        const isFirstCalibration = !this.hasCompletedCalibration;

        if (isFirstCalibration || forceReset) {
            if (userId) {
                // Gather necessary data for baseline (using the just calculated values)
                const baselineData = {
                    score: this.score, // Use final score after level 10
                    averageAccuracy: accuracy, // Use the accuracy calculated for level 10? Or average? Let's use current for simplicity now. Revisit if avg needed.
                    averageCompletionTime: completionTime, // Use time for level 10? Or average? Let's use current. Revisit if avg needed.
                    successfulShortcuts: this.analyticsData.successfulShortcuts || 0 // Use successful shortcuts from level 10
                    // Note: Baseline ideally uses average of levels 1-10, but requires more complex aggregation.
                    // This simpler approach saves stats *after completing* level 10.
                };

                savePlayerBaseline(userId, baselineData, forceReset)
                    .then((saved) => {
                        if (saved) {
                            this.hasCompletedCalibration = true;
                            // ENHANCED: Set user-specific calibration completion
                            const completionUser = auth.currentUser;
                            const completionCalibrationKey = completionUser ? `hasCompletedCalibration_${completionUser.uid}` : 'hasCompletedCalibration_guest';
                            localStorage.setItem(completionCalibrationKey, 'true');
                            // ENHANCED: Remove user-specific force reset flag
                            if (forceReset) {
                                const completionUser = auth.currentUser;
                                const forceResetKey = completionUser ? `forceCalibrationReset_${completionUser.uid}` : 'forceCalibrationReset_guest';
                                localStorage.removeItem(forceResetKey);
                            }
                            console.log("Baseline saved successfully");
                            // Message shown in levelCleared/showRoundOneSummary
                        }
                    })
                    .catch(error => console.error("Failed to save baseline:", error));
            }
        } else {
             console.log("Calibration round completed, but baseline not updated (not first time or forced reset).");
             // Message shown in showRoundOneSummary
        }
    }
    // --- End Baseline Saving Logic ---
  }
  // --- END REPLACE saveLevelAnalytics ---

  // Next level logic
// Update the nextLevelImmediate method
nextLevelImmediate() {
  // If we've just finished level 10, stop everything and show round summary
  if (this.currentLevel === 10) {
    // Completely stop automatic progression
    this.levelComplete = true; // Prevent further updates
    this.time.removeAllEvents(); // Remove all timers
    this.physics.pause(); // Pause physics
    
    // Small delay to ensure everything is stopped
    setTimeout(() => {
      this.showRoundOneSummary();
    }, 100);
    return; // Don't proceed to level 11
  }
  
  // For all other level transitions, proceed normally
  this.currentLevel++;

  // If we just finished Level 0 => show "Round 1 Intro"
  if (this.currentLevel === 1) {
    return this.showRoundOneIntro();
  }

  // If we just finished Level 20 => end Round 2 (final)
  if (this.currentLevel === 21) {
    return this.showRoundTwoSummary(); // final
  }

  // Otherwise, proceed normally
  this.startLevel(this.currentLevel);
}


  // Round 1 intro with analytics context
  showRoundOneIntro() {
    // Clear shapes
    this.allShapes.forEach((s) => s.destroy());
    this.allShapes = [];

    // Show a big text
    const centerX = this.cameras.main.width / 2;
    const centerY = this.cameras.main.height / 2;
    const introText = this.add
      .text(
        centerX,
        centerY - 50,
        "ROUND 1: Calibration Round\n(Levels 1–10)",
        {
          fontSize: "28px",
          color: "#000",
          align: "center",
        }
      )
      .setOrigin(0.5);

    // Add additional text explaining calibration purpose
    const calibrationExplain = this.add
      .text(
        centerX,
        centerY,
        "This round will establish your baseline performance.\nYour results will be used to track your progress over time.",
        {
          fontSize: "18px",
          color: "#000",
          align: "center",
        }
      )
      .setOrigin(0.5);

    // Add a "Begin Round 1" button
    const btn = this.add
      .text(centerX, centerY + 80, "[ Begin Round 1 ]", {
        fontSize: "24px",
        color: "#fff",
        backgroundColor: "#007BFF",
        padding: { x: 10, y: 5 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
      
    btn.on("pointerdown", () => {
      introText.destroy();
      calibrationExplain.destroy();
      btn.destroy();
      this.currentLevel = 1;
      this.startLevel(1);
    });
  }

  // In the showRoundOneSummary method of PPTHeroScene.js
  showRoundOneSummary() {
    // Make sure the game is fully stopped
    this.time.removeAllEvents(); // Remove all existing timers
    this.physics.pause(); // Pause physics engine
    this.countdownEvent = null; // Clear any countdown
    this.topMessage = null; // Clear any message
    this.levelComplete = true; // Set level as complete to prevent updates
    
    // Clear shapes
    this.allShapes.forEach((s) => s.destroy());
    this.allShapes = [];
  
    // Show partial scoreboard
    const centerX = this.cameras.main.width / 2;
    const centerY = this.cameras.main.height / 2;
  
    // Update UI text to not show level 11
    this.uiText.setText("Round 1 Complete!");
  
    const summaryText = this.add
      .text(
        centerX,
        centerY - 80,
        `ROUND 1 COMPLETE!\nYour Score So Far: ${this.score}`,
        {
          fontSize: "24px",
          color: "#000",
          align: "center",
        }
      )
      .setOrigin(0.5);
      
    // Add analytics text
    const analyticsText = this.add
      .text(
        centerX,
        centerY - 20,
        `Your baseline performance has been saved.\nCheck the Analytics button to track your progress!`,
        {
          fontSize: "18px",
          color: "#000",
          align: "center",
        }
      )
      .setOrigin(0.5);
  
    // Check user verification
    const user = auth.currentUser;
    const isVerified = user && user.emailVerified;
  
    if (!isVerified) {
      // Not verified => must end game
      this.time.delayedCall(1500, () => {
        summaryText.setText("Unverified user => Game Over");
        analyticsText.setText("Verify your account to track your progress over time!");
        // Slight delay then show gameOver
        this.time.delayedCall(1500, () => {
          saveScoreToFirestore(this.score, "PPTHero").then(() => {
            showGameOverUI(this.score, "PPTHero");
          });
        });
      });
      return;
    }
  
    // If verified => offer 2 buttons
    const btnNext = this.add
      .text(centerX, centerY + 40, "[ Start Next Round ]", {
        fontSize: "20px",
        color: "#fff",
        backgroundColor: "#28a745",
        padding: { x: 8, y: 4 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
  
    btnNext.on("pointerdown", () => {
      summaryText.destroy();
      analyticsText.destroy();
      btnNext.destroy();
      btnEnd.destroy();
      
      // Reset level completion flag
      this.levelComplete = false;
      
      // Start level 11
      this.currentLevel = 11;
      this.startLevel(11);
    });
  
    const btnEnd = this.add
      .text(centerX, centerY + 90, "[ End Game ]", {
        fontSize: "20px",
        color: "#fff",
        backgroundColor: "#dc3545",
        padding: { x: 8, y: 4 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
  
    btnEnd.on("pointerdown", () => {
      summaryText.destroy();
      analyticsText.destroy();
      btnNext.destroy();
      btnEnd.destroy();
      
      // End game => show overlay
      saveScoreToFirestore(this.score, "PPTHero").then(() => {
        showGameOverUI(this.score, "PPTHero");
      });
    });
  }
  // Final round summary with analytics
  showRoundTwoSummary() {
    // Clear shapes
    this.allShapes.forEach((s) => s.destroy());
    this.allShapes = [];

    // Show final scoreboard
    const centerX = this.cameras.main.width / 2;
    const centerY = this.cameras.main.height / 2;

    // Compute performance rating
    let performanceRating = "Average";
    if (this.score >= 1200) performanceRating = "Excellent";
    else if (this.score >= 800) performanceRating = "Good";

    const finalText = `ROUND 2 COMPLETE!\nTotal Score: ${this.score}\nPerformance: ${performanceRating}`;
    this.add
      .text(centerX, centerY - 60, finalText, {
        fontSize: "24px",
        color: "#000",
        align: "center",
      })
      .setOrigin(0.5);
      
    // Add analytics prompt
    this.add
      .text(centerX, centerY, "Check the Analytics button to see how you've improved!", {
        fontSize: "18px",
        color: "#000",
        align: "center",
      })
      .setOrigin(0.5);

    // Save final score => show overlay
    this.time.delayedCall(2000, () => {
      saveScoreToFirestore(this.score, "PPTHero").then(() => {
        showGameOverUI(this.score, "PPTHero");
      });
    });
  }

  // Create shape
  createShape(x, y, w, h, color, isFixed = false, isDecoration = false) {
    const rect = this.add.rectangle(x, y, w, h, color);
    rect.setOrigin(0.5, 0.5);

      // Set initial depth based on the current shape count
  rect.setDepth(this.allShapes.length * 10 + 10);
  
  rect.setInteractive({ useHandCursor: true });
  this.input.setDraggable(rect);
  this.allShapes.push(rect);
    
    // Only make it interactive and draggable if it's not a fixed element
    if (!isFixed) {
      rect.setInteractive({ useHandCursor: true });
      this.input.setDraggable(rect);
    }
    
    // Store metadata about the shape
    if (isDecoration) {
      rect.setData('isDecoration', true);
    }
    if (isFixed) {
      rect.setData('isFixed', true);
    }
    
    
    return rect;
  }
  // ==============================
  // POINTER EVENT HANDLERS
  // ==============================
  
  // Pointer down with analytics tracking
// Now update the onPointerDown method to filter objects with isFixed or isDecoration flags
// Add this to PPTHeroScene.js, replacing the existing onPointerDown method

onPointerDown(pointer, objectsUnderPointer) {
  this.levelClicks++;
  
  // Initialize analyticsData if it doesn't exist
  if (!this.analyticsData) {
    this.analyticsData = {
      levelStartTime: Date.now() / 1000,
      shortcutAttempts: 0,
      successfulShortcuts: 0,
      mouseClicks: 0,
      keystrokeCount: 0,
      errors: {
        misstypedShortcuts: 0,
        incorrectSequences: 0,
        missclicks: 0
      },
      shortcutsUsed: {},
      shortcutsMissed: {}
    };
  }
  
  // Track mouse clicks
  this.analyticsData.mouseClicks++;
  
  // Filter out fixed and decorative elements (like dividers)
  const clickableObjects = [];
  for (let i = 0; i < objectsUnderPointer.length; i++) {
    const obj = objectsUnderPointer[i];
    // Only include objects that don't have isFixed or isDecoration flags
    if (!obj.getData || (!obj.getData('isFixed') && !obj.getData('isDecoration'))) {
      clickableObjects.push(obj);
    }
  }

  if (clickableObjects.length === 0) {
    // Clicked empty space or non-interactive element - potentially a missclick
    if (!this.isMarqueeSelecting && this.selectedShapes.length > 0) {
      // If shapes are already selected and not starting a marquee selection,
      // this might be a missclick (user intended to click on a shape)
      this.analyticsData.errors.missclicks++;
    }

    // Clicked empty space - start marquee selection
    if (!pointer.event.shiftKey) {
      this.clearSelection();
    }
    this.isMarqueeSelecting = true;
    this.marqueeStartX = pointer.x;
    this.marqueeStartY = pointer.y;
    this.marqueeRect.setPosition(pointer.x, pointer.y);
    this.marqueeRect.setSize(0, 0);
    this.marqueeRect.setVisible(true);
  } else {
    // Clicked on shape(s)
    const topObj = clickableObjects[clickableObjects.length - 1];
    if (!this.allShapes.includes(topObj)) return;

    pointer.event.stopPropagation();
    if (pointer.event.shiftKey) {
      // Toggle selection
      if (this.selectedShapes.includes(topObj)) {
        this.deselectShape(topObj);
      } else {
        this.selectShape(topObj);
      }
    } else {
      // Normal click => clear & select
      if (!this.selectedShapes.includes(topObj)) {
        this.clearSelection();
        this.selectShape(topObj);
      }
    }
  }
}
  onPointerMove(pointer) {
    if (this.isMarqueeSelecting) {
      const x = Math.min(this.marqueeStartX, pointer.x);
      const y = Math.min(this.marqueeStartY, pointer.y);
      const w = Math.abs(pointer.x - this.marqueeStartX);
      const h = Math.abs(pointer.y - this.marqueeStartY);
      this.marqueeRect.setPosition(x, y);
      this.marqueeRect.setSize(w, h);
    }
  }

  onPointerUp() {
    if (this.isMarqueeSelecting) {
      this.isMarqueeSelecting = false;
      const mx = this.marqueeRect.x;
      const my = this.marqueeRect.y;
      const mw = this.marqueeRect.displayWidth;
      const mh = this.marqueeRect.displayHeight;

      this.allShapes.forEach((shape) => {
        const halfW = shape.displayWidth / 2;
        const halfH = shape.displayHeight / 2;
        const left = shape.x - halfW;
        const right = shape.x + halfW;
        const top = shape.y - halfH;
        const bottom = shape.y + halfH;
        if (right > mx && left < mx + mw && bottom > my && top < my + mh) {
          this.selectShape(shape);
        }
      });

      this.marqueeRect.setVisible(false);
    }
  }

  // ==============================
  // DRAG HANDLERS
  // ==============================
  
  onDragStart(pointer, gameObject) {
    if (this.selectedShapes.includes(gameObject)) {
      // Group drag
      this.selectedShapes.forEach((s) => s.setStrokeStyle(2, 0xff0000));
      this.groupDragStart = {
        pointerX: pointer.x,
        pointerY: pointer.y,
        positions: this.selectedShapes.map((s) => ({ shape: s, x: s.x, y: s.y })),
      };
    } else {
      this.groupDragStart = null;
      gameObject.setData("offsetX", gameObject.x - pointer.x);
      gameObject.setData("offsetY", gameObject.y - pointer.y);
    }
  }

  onDrag(pointer, gameObject, dragX, dragY) {
    if (this.groupDragStart && this.selectedShapes.includes(gameObject)) {
      // Move entire selected group
      const dx = pointer.x - this.groupDragStart.pointerX;
      const dy = pointer.y - this.groupDragStart.pointerY;
      this.groupDragStart.positions.forEach((item) => {
        let newX = item.x + dx;
        let newY = item.y + dy;
        const halfW = item.shape.displayWidth / 2;
        const halfH = item.shape.displayHeight / 2;
        newX = Phaser.Math.Clamp(newX, halfW, this.cameras.main.width - halfW);
        newY = Phaser.Math.Clamp(newY, halfH, this.cameras.main.height - halfH);
        item.shape.x = newX;
        item.shape.y = newY;
      });
    } else {
      // Single shape drag
      const halfW = gameObject.displayWidth / 2;
      const halfH = gameObject.displayHeight / 2;
      let newX = Phaser.Math.Clamp(dragX, halfW, this.cameras.main.width - halfW);
      let newY = Phaser.Math.Clamp(dragY, halfH, this.cameras.main.height - halfH);
      gameObject.x = newX;
      gameObject.y = newY;
    }
  }

  onDragEnd(pointer, gameObject) {
    this.groupDragStart = null;
  }

  // ==============================
  // SELECTION MANAGEMENT
  // ==============================
  
  selectShape(shape) {
    if (!this.selectedShapes.includes(shape)) {
      this.selectedShapes.push(shape);
    }
    shape.setStrokeStyle(2, 0xff0000);
  }

  deselectShape(shape) {
    Phaser.Utils.Array.Remove(this.selectedShapes, shape);
    shape.setStrokeStyle();
  }

  clearSelection() {
    // Only clear selection for non-fixed, non-decoration shapes
    this.selectedShapes.forEach((s) => {
      if (!s.getData('isFixed') && !s.getData('isDecoration')) {
        s.setStrokeStyle();
      }
    });
    this.selectedShapes = [];
  }
  
  // Update the selectShape method to prevent selecting fixed/decoration elements
  selectShape(shape) {
    // Don't select fixed or decorative elements
    if (shape.getData('isFixed') || shape.getData('isDecoration')) {
      return;
    }
    
    if (!this.selectedShapes.includes(shape)) {
      this.selectedShapes.push(shape);
    }
    shape.setStrokeStyle(2, 0xff0000);
  }

  // ==============================
  // SHORTCUT HANDLING
  // ==============================
  
  handleShortcuts(event) {
    this.levelKeystrokes++;
    
    // Ensure analyticsData exists
    if (!this.analyticsData) {
      this.analyticsData = {
        levelStartTime: Date.now() / 1000,
        levelCompletionTime: 0,
        shortcutAttempts: 0,
        successfulShortcuts: 0,
        mouseClicks: 0,
        keystrokeCount: 0,
        errors: {
          misstypedShortcuts: 0,
          incorrectSequences: 0,
          missclicks: 0
        },
        shortcutsUsed: {},
        shortcutsMissed: {}
      };
    }
    
    this.analyticsData.keystrokeCount++;
    
    // Check if event and event.key exist
    if (!event || !event.key) return;
  
    // Debug log key presses
    console.log(`Key pressed: ${event.key}, Alt: ${event.altKey}, Ctrl: ${event.ctrlKey}, Shift: ${event.shiftKey}`);
  
    // Quick check for Ctrl + A => select all
    if (event.ctrlKey && event.key.toLowerCase() === "a") {
      event.preventDefault();
      event.stopPropagation();
      this.selectAllShapes();
      this.analyticsData.shortcutAttempts++;
      this.analyticsData.successfulShortcuts++;
      
      // Track shortcut usage
      const shortcutName = "ctrl+a";
      if (!this.analyticsData.shortcutsUsed[shortcutName]) {
        this.analyticsData.shortcutsUsed[shortcutName] = 0;
      }
      this.analyticsData.shortcutsUsed[shortcutName]++;
      
      // Log successful shortcut for debugging
      console.log("Executed shortcut: CTRL+A (Select All)");
      return;
    }

    // Check for Ctrl+Shift+] (bring to front)
if (event.ctrlKey && event.shiftKey && (event.key === ']' || event.key === '}')) {
  event.preventDefault();
  event.stopPropagation();
  console.log("Executing shortcut: Ctrl+Shift+] (Bring to Front)");
  
  this.bringSelectionToFront();
  this.analyticsData.shortcutAttempts++;
  this.analyticsData.successfulShortcuts++;
  
  // Track shortcut usage
  const shortcutName = "ctrl+shift+]";
  if (!this.analyticsData.shortcutsUsed[shortcutName]) {
    this.analyticsData.shortcutsUsed[shortcutName] = 0;
  }
  this.analyticsData.shortcutsUsed[shortcutName]++;
  return;
}

// Check for Ctrl+Shift+[ (send to back)
if (event.ctrlKey && event.shiftKey && (event.key === '[' || event.key === '{')) {
  event.preventDefault();
  event.stopPropagation();
  console.log("Executing shortcut: Ctrl+Shift+[ (Send to Back)");
  
  this.sendSelectionToBack();
  this.analyticsData.shortcutAttempts++;
  this.analyticsData.successfulShortcuts++;
  
  // Track shortcut usage
  const shortcutName = "ctrl+shift+[";
  if (!this.analyticsData.shortcutsUsed[shortcutName]) {
    this.analyticsData.shortcutsUsed[shortcutName] = 0;
  }
  this.analyticsData.shortcutsUsed[shortcutName]++;
  return;
}
  
    // Next: layering shortcuts
    if (event.ctrlKey && event.shiftKey && event.key === "[") {
      this.sendSelectionToBack();
      this.analyticsData.shortcutAttempts++;
      this.analyticsData.successfulShortcuts++;
      
      // Track shortcut usage
      const shortcutName = "ctrl+shift+[";
      if (!this.analyticsData.shortcutsUsed[shortcutName]) {
        this.analyticsData.shortcutsUsed[shortcutName] = 0;
      }
      this.analyticsData.shortcutsUsed[shortcutName]++;
      
      console.log("Executed shortcut: CTRL+SHIFT+[ (Send to Back)");
      return;
    }
    
    if (event.ctrlKey && event.shiftKey && event.key === "]") {
      this.bringSelectionToFront();
      this.analyticsData.shortcutAttempts++;
      this.analyticsData.successfulShortcuts++;
      
      // Track shortcut usage
      const shortcutName = "ctrl+shift+]";
      if (!this.analyticsData.shortcutsUsed[shortcutName]) {
        this.analyticsData.shortcutsUsed[shortcutName] = 0;
      }
      this.analyticsData.shortcutsUsed[shortcutName]++;
      
      console.log("Executed shortcut: CTRL+SHIFT+] (Bring to Front)");
      return;
    }
  
    // Multi-key combos
    const currentTime = this.time.now;
    
    // Reset sequence if timeout has passed
    if (currentTime - this.lastKeyTime > this.sequenceTimeout) {
      console.log("Timeout exceeded, resetting sequence");
      this.shortcutSequence = [];
    }
    
    this.lastKeyTime = currentTime;
    
    // Handle Alt key specifically - it often initiates sequences
    if (event.key === "Alt" || event.key === "alt") {
      event.preventDefault();
      event.stopPropagation();
      
      // Reset sequence and start a new one with alt
      console.log("Alt key pressed, starting new sequence");
      this.shortcutSequence = ["alt"];
      return;
    }
    
    // If Alt is being held but not in sequence, add it
    if (event.altKey && this.shortcutSequence.length === 0) {
      console.log("Alt modifier active, adding to sequence");
      this.shortcutSequence.push("alt");
    }
  
    // Handle meta key (Command on Mac)
    if (event.key === "Meta") {
      event.preventDefault();
      event.stopPropagation();
      console.log("Meta key pressed, adding to sequence");
      this.shortcutSequence.push("meta");
      return;
    }
  
    // Add the current key to the sequence if not already handled
    const pressed = event.key ? event.key.toLowerCase() : '';
    if (pressed && !["alt", "meta", "control", "shift"].includes(pressed)) {
      console.log(`Adding key to sequence: ${pressed}`);
      this.shortcutSequence.push(pressed);
    }
  
    // Log current sequence for debugging
    console.log("Current sequence:", this.shortcutSequence.join(","));
  
    // Define all supported shortcut combinations
    const combos = {
      "alt,h,g,a,l": () => this.alignLeft(),
      "alt,h,g,a,r": () => this.alignRight(),
      "alt,h,g,a,t": () => this.alignTop(),
      "alt,h,g,a,b": () => this.alignBottom(),
      "alt,h,g,a,c": () => this.alignCenterHorizontally(),
      "alt,h,g,a,m": () => this.alignCenterVertically(),
      "alt,h,g,a,h": () => this.distributeHorizontally(),
      "alt,h,g,a,v": () => this.distributeVertically(),
    };
  
    // Get current sequence string
    const seqStr = this.shortcutSequence.join(",");
    
    // Check if current sequence matches any combo
    if (combos[seqStr]) {
      // Count the attempt and execution
      this.analyticsData.shortcutAttempts++;
      this.analyticsData.successfulShortcuts++;
      
      // Track successful shortcut usage
      if (!this.analyticsData.shortcutsUsed[seqStr]) {
        this.analyticsData.shortcutsUsed[seqStr] = 0;
      }
      this.analyticsData.shortcutsUsed[seqStr]++;
      
      // Execute the shortcut function
      console.log(`Executing shortcut: ${seqStr}`);
      combos[seqStr]();
      
      // Reset sequence after execution
      this.shortcutSequence = [];
      return;
    }
    
    // Check if it's a valid prefix of any shortcut
    const validPrefix = Object.keys(combos).some(k => k.startsWith(seqStr));
    console.log(`Sequence is ${validPrefix ? 'a valid' : 'not a valid'} prefix`);
    
    // If not a valid prefix and we've typed at least 2 keys, reset
    if (!validPrefix && this.shortcutSequence.length > 1) {
      // This was likely an attempted shortcut that failed
      this.analyticsData.shortcutAttempts++;
      
      // Track the attempted sequence
      const attemptedSeq = this.shortcutSequence.join(",");
      
      // Track missed shortcuts
      if (!this.analyticsData.shortcutsMissed[attemptedSeq]) {
        this.analyticsData.shortcutsMissed[attemptedSeq] = 0;
      }
      this.analyticsData.shortcutsMissed[attemptedSeq]++;
      
      // Classify error type
      if (this.shortcutSequence.length >= 3) {
        this.analyticsData.errors.incorrectSequences++;
      } else {
        this.analyticsData.errors.misstypedShortcuts++;
      }
      
      // Reset sequence
      console.log("Invalid sequence detected, resetting");
      this.shortcutSequence = [];
    }
  }
  
  // ==============================
  // SELECTION UTILITIES
  // ==============================
  
  selectAllShapes() {
    this.clearSelection();
    this.allShapes.forEach((shape) => {
      this.selectedShapes.push(shape);
      shape.setStrokeStyle(2, 0xff0000);
    });
  }

  // ==============================
  // LAYERING
  // ==============================
  
// Improved sendSelectionToBack method
// Completely revised bringSelectionToFront method
// Fixed bringSelectionToFront method
// Completely revised bringSelectionToFront method
// Direct approach to bringSelectionToFront
bringSelectionToFront() {
  if (this.selectedShapes.length === 0) return;
  
  // First ensure all shapes have proper depths
  this.normalizeDepths();
  
  // For each selected shape
  for (const shape of this.selectedShapes) {
    // Find all shapes
    let highestDepth = -1;
    let shapeAbove = null;
    
    // Find the shape with the next highest depth
    for (const otherShape of this.allShapes) {
      if (otherShape === shape) continue;
      
      if (otherShape.depth > shape.depth && 
          (shapeAbove === null || otherShape.depth < shapeAbove.depth)) {
        shapeAbove = otherShape;
      }
      
      if (otherShape.depth > highestDepth) {
        highestDepth = otherShape.depth;
      }
    }
    
    // If no shape above, move to top
    if (!shapeAbove) {
      shape.setDepth(highestDepth + 10);
      continue;
    }
    
    // Find shapes between current shape and shape above
    const shapesBetween = this.allShapes.filter(s => 
      s !== shape && 
      s !== shapeAbove && 
      s.depth > shape.depth && 
      s.depth < shapeAbove.depth
    );
    
    // If no shapes between, swap depths
    if (shapesBetween.length === 0) {
      const tempDepth = shape.depth;
      shape.setDepth(shapeAbove.depth);
      shapeAbove.setDepth(tempDepth);
    } else {
      // If shapes between, move just below the shape above
      shape.setDepth(shapeAbove.depth - 1);
    }
  }
  
  console.log("Moved selection one layer forward");
}

// Helper to normalize all depths
normalizeDepths() {
  // Sort shapes by current depth
  const sortedShapes = [...this.allShapes].sort((a, b) => a.depth - b.depth);
  
  // Reassign depths with spacing of 10
  sortedShapes.forEach((shape, index) => {
    shape.setDepth((index + 1) * 10);
  });
}

// Completely revised sendSelectionToBack method
sendSelectionToBack() {
  if (this.selectedShapes.length === 0) return;
  
  // First ensure all shapes have proper depth values
  this.ensureProperDepths();
  
  // Sort shapes by depth (lowest to highest)
  const sortedShapes = [...this.allShapes].sort((a, b) => a.depth - b.depth);
  
  // Process selected shapes from lowest to highest depth to avoid conflicts
  const selectedSorted = [...this.selectedShapes].sort((a, b) => a.depth - b.depth);
  
  // For each selected shape
  for (const shape of selectedSorted) {
    // Find its index in the sorted array
    const currentIndex = sortedShapes.findIndex(s => s === shape);
    
    // If it's already at the bottom, skip it
    if (currentIndex === 0) continue;
    
    // Get the shape directly below it
    const belowShape = sortedShapes[currentIndex - 1];
    
    // Store current depths
    const currentDepth = shape.depth;
    const belowDepth = belowShape.depth;
    
    // Swap depths - this is the key for moving exactly one layer
    shape.setDepth(belowDepth);
    belowShape.setDepth(currentDepth);
    
    // Update the sorted array to reflect our changes
    sortedShapes[currentIndex] = belowShape;
    sortedShapes[currentIndex - 1] = shape;
  }
  
  console.log("Moved selection one layer backward");
}

// Helper method to ensure all shapes have consistent depth values
ensureProperDepths() {
  // Sort shapes by current depth (or 0 if undefined)
  const sortedShapes = [...this.allShapes].sort((a, b) => 
    (a.depth || 0) - (b.depth || 0)
  );
  
  // Reassign depths with spacing of 10 between each
  sortedShapes.forEach((shape, index) => {
    shape.setDepth((index + 1) * 10);
  });
}
  // ==============================
  // ALIGNMENT & DISTRIBUTION
  // ==============================
  
 // ==============================
  // ALIGNMENT & DISTRIBUTION
  // ==============================
  
  alignLeft() {
    console.log("Aligning left", this.selectedShapes.length, "shapes");
    if (this.selectedShapes.length < 1) {
      console.log("Not enough shapes selected");
      return;
    }
    
    let minLeft = Infinity;
    this.selectedShapes.forEach((s) => {
      const left = s.x - s.displayWidth / 2;
      if (left < minLeft) minLeft = left;
    });
    
    this.selectedShapes.forEach((s) => {
      s.x = minLeft + s.displayWidth / 2;
    });
  }

  alignRight() {
    console.log("Aligning right", this.selectedShapes.length, "shapes");
    if (this.selectedShapes.length < 1) {
      console.log("Not enough shapes selected");
      return;
    }
    
    let maxRight = -Infinity;
    this.selectedShapes.forEach((s) => {
      const right = s.x + s.displayWidth / 2;
      if (right > maxRight) maxRight = right;
    });
    
    this.selectedShapes.forEach((s) => {
      s.x = maxRight - s.displayWidth / 2;
    });
  }

  alignTop() {
    console.log("Aligning top", this.selectedShapes.length, "shapes");
    if (this.selectedShapes.length < 1) {
      console.log("Not enough shapes selected");
      return;
    }
    
    let minTop = Infinity;
    this.selectedShapes.forEach((s) => {
      const top = s.y - s.displayHeight / 2;
      if (top < minTop) minTop = top;
    });
    
    this.selectedShapes.forEach((s) => {
      s.y = minTop + s.displayHeight / 2;
    });
  }

  alignBottom() {
    console.log("Aligning bottom", this.selectedShapes.length, "shapes");
    if (this.selectedShapes.length < 1) {
      console.log("Not enough shapes selected");
      return;
    }
    
    let maxBottom = -Infinity;
    this.selectedShapes.forEach((s) => {
      const bottom = s.y + s.displayHeight / 2;
      if (bottom > maxBottom) maxBottom = bottom;
    });
    
    this.selectedShapes.forEach((s) => {
      s.y = maxBottom - s.displayHeight / 2;
    });
  }

// Horizontal centering - centers shapes relative to their collective bounding box
alignCenterHorizontally() {
  console.log("Aligning center horizontally", this.selectedShapes.length, "shapes");
  if (this.selectedShapes.length < 1) {
    console.log("Not enough shapes selected");
    return;
  }
  
  // Find the bounding box of selected shapes
  let minX = Infinity;
  let maxX = -Infinity;
  
  this.selectedShapes.forEach((shape) => {
    const left = shape.x - shape.displayWidth / 2;
    const right = shape.x + shape.displayWidth / 2;
    
    if (left < minX) minX = left;
    if (right > maxX) maxX = right;
  });
  
  // Calculate center of the bounding box
  const centerX = minX + (maxX - minX) / 2;
  
  // Align all shapes to this center point
  this.selectedShapes.forEach((shape) => {
    shape.x = centerX;
  });
}

// Vertical centering - centers shapes relative to their collective bounding box
alignCenterVertically() {
  console.log("Aligning center vertically", this.selectedShapes.length, "shapes");
  if (this.selectedShapes.length < 1) {
    console.log("Not enough shapes selected");
    return;
  }
  
  // Find the bounding box of selected shapes
  let minY = Infinity;
  let maxY = -Infinity;
  
  this.selectedShapes.forEach((shape) => {
    const top = shape.y - shape.displayHeight / 2;
    const bottom = shape.y + shape.displayHeight / 2;
    
    if (top < minY) minY = top;
    if (bottom > maxY) maxY = bottom;
  });
  
  // Calculate center of the bounding box
  const centerY = minY + (maxY - minY) / 2;
  
  // Align all shapes to this center point
  this.selectedShapes.forEach((shape) => {
    shape.y = centerY;
  });
}

  distributeHorizontally() {
    console.log("Distributing horizontally", this.selectedShapes.length, "shapes");
    if (this.selectedShapes.length < 3) {
      console.log("Need at least 3 shapes to distribute");
      return;
    }
    
    const sorted = [...this.selectedShapes].sort((a, b) => a.x - b.x);
    const leftX = sorted[0].x;
    const rightX = sorted[sorted.length - 1].x;
    
    for (let i = 1; i < sorted.length - 1; i++) {
      const fraction = i / (sorted.length - 1);
      sorted[i].x = Math.round(leftX + (rightX - leftX) * fraction);
    }
  }

  distributeVertically() {
    console.log("Distributing vertically", this.selectedShapes.length, "shapes");
    if (this.selectedShapes.length < 3) {
      console.log("Need at least 3 shapes to distribute");
      return;
    }
    
    const sorted = [...this.selectedShapes].sort((a, b) => a.y - b.y);
    const topY = sorted[0].y;
    const bottomY = sorted[sorted.length - 1].y;
    
    for (let i = 1; i < sorted.length - 1; i++) {
      const fraction = i / (sorted.length - 1);
      sorted[i].y = topY + (bottomY - topY) * fraction;
    }
  }
}